<template>
    <div class="pl-page" :style="{'max-width': '99%', 'width': '98%', 'margin-left': 'auto', 'margin-right': 'auto', 'margin-top': '10px'}">

        <!-- Barcode Viewer Modal -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-barcode-viewer" size="md" hide-footer centered title="Product Barcode">
            <div class="text-center">
                <b-img :src="currentBarcodeImage" fluid alt="Product barcode" style="max-height: 70vh; object-fit: contain;" />
                <div class="mt-3">
                    <strong>{{ currentProductName }}</strong>
                    <div class="small text-muted">{{ currentProductSku }}</div>
                </div>
                <div class="mt-2">
                    <b-button size="sm" variant="outline-primary" @click="downloadBarcodeImage(currentBarcodeImage, currentProductSku)">
                        <b-icon icon="download" aria-hidden="true"></b-icon>
                        Download Barcode
                    </b-button>
                </div>
            </div>
            <template #modal-footer>
                <b-button size="sm" variant="secondary" @click="$bvModal.hide('modal-barcode-viewer')">Close</b-button>
            </template>
        </b-modal>

        <!-- Image Viewer Modal -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-image-viewer" size="lg" hide-footer centered title="Product Image">
            <div class="text-center">
                <b-img :src="currentFullSizeImage" fluid alt="Full size product image" style="max-height: 70vh; object-fit: contain;" />
                <div class="mt-3"><strong>{{ currentProductName }}</strong></div>
            </div>
            <template #modal-footer>
                <b-button size="sm" variant="secondary" @click="$bvModal.hide('modal-image-viewer')">Close</b-button>
            </template>
        </b-modal>

        <!-- Product Stats Modal -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-product-stats" scrollable title="PRODUCT STATS" size="xl" hide-footer style="min-height: 700px;">
            <ProductStatsModal
                v-on:done-product-stats="close_item_stats"
                v-on:changed-product="monitor_modal_change"
                style="min-height: 500px;"
                :viewdata="stats_item"
            />
        </b-modal>

        <!-- Sold Details Modal -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-sold-details" scrollable :title="`SOLD DETAILS - ${currentProduct ? currentProduct.product_name : ''} (${currentProduct ? currentProduct.sku : ''})`" size="lg" hide-footer>
            <div class="details-container">
                <b-table striped hover :items="soldDetails" :fields="detailFields">
                    <template #cell(value)="data">
                        <b-badge variant="success">{{ data.value }}</b-badge>
                    </template>
                </b-table>
            </div>
        </b-modal>

        <!-- Order History Modal -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-order-details"
                scrollable
                :title="`ORDER HISTORY - ${currentProduct ? currentProduct.product_name : ''} (${currentProduct ? currentProduct.sku : ''})`"
                size="xl"
                hide-footer>
            <div v-if="isLoadingOrderDetails" class="text-center py-5">
                <b-spinner variant="primary"></b-spinner>
                <p class="mt-2">Loading order history...</p>
            </div>
            <div v-else-if="orderHistory && orderHistory.length > 0">
                <b-row class="mb-3">
                    <b-col md="3">
                        <b-card class="text-center" bg-variant="light">
                            <h5>{{ orderStats.total_orders }}</h5>
                            <small>Total Orders</small>
                        </b-card>
                    </b-col>
                    <b-col md="3">
                        <b-card class="text-center" bg-variant="light">
                            <h5>{{ orderStats.total_quantity }}</h5>
                            <small>Total Quantity</small>
                        </b-card>
                    </b-col>
                    <b-col md="3">
                        <b-card class="text-center" bg-variant="light">
                            <h5>{{ Object.keys(orderStats.by_status).join(', ') }}</h5>
                            <small>Status Distribution</small>
                        </b-card>
                    </b-col>
                    <b-col md="3">
                        <b-card class="text-center" bg-variant="light">
                            <h5>{{ moment(orderHistory[0].timeline[0].date).fromNow() }}</h5>
                            <small>Last Order</small>
                        </b-card>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="12">
                        <h6>Complete Order History</h6>
                        <div style="max-height: 400px; overflow-y: auto;">
                            <b-table striped hover small :items="allOrderEvents" :fields="eventFields">
                                <template #cell(date)="data">
                                    {{ moment(data.value).format('YYYY-MM-DD HH:mm') }}
                                </template>
                                <template #cell(status)="data">
                                    <b-badge :variant="getStatusVariant(data.value)">{{ data.value }}</b-badge>
                                </template>
                                <template #cell(quantity)="data">
                                    <b-badge variant="info">{{ data.value }}</b-badge>
                                </template>
                            </b-table>
                        </div>
                    </b-col>
                </b-row>
            </div>
            <div v-else class="text-center py-5">
                <b-icon icon="inbox" font-scale="3" variant="secondary"></b-icon>
                <h5 class="mt-3">No order history found</h5>
                <p class="text-muted">No orders have been placed for this product yet</p>
            </div>
            <template #modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('modal-order-details')">Close</b-button>
            </template>
        </b-modal>

        <!-- Received History Modal -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-received-details"
                scrollable
                :title="`RECEIVED HISTORY - ${currentProduct ? currentProduct.product_name : ''} (${currentProduct ? currentProduct.sku : ''})`"
                size="xl"
                hide-footer>
            <div v-if="isLoadingReceivedDetails" class="text-center py-5">
                <b-spinner variant="primary"></b-spinner>
                <p class="mt-2">Loading received history...</p>
            </div>
            <div v-else-if="receivedHistory && receivedHistory.length > 0">
                <b-row class="mb-3">
                    <b-col md="3">
                        <b-card class="text-center" bg-variant="light">
                            <h5>{{ receivedStats.total_received }}</h5>
                            <small>Total Received Events</small>
                        </b-card>
                    </b-col>
                    <b-col md="3">
                        <b-card class="text-center" bg-variant="light">
                            <h5>{{ receivedStats.total_quantity }}</h5>
                            <small>Total Quantity</small>
                        </b-card>
                    </b-col>
                    <b-col md="3">
                        <b-card class="text-center" bg-variant="light">
                            <h5>{{ Object.keys(receivedStats.by_type).join(', ') }}</h5>
                            <small>Order Types</small>
                        </b-card>
                    </b-col>
                    <b-col md="3">
                        <b-card class="text-center" bg-variant="light">
                            <h5>{{ receivedHistory[0] && receivedHistory[0].timeline && receivedHistory[0].timeline.length > 0 ? moment(receivedHistory[0].timeline[0].date).fromNow() : 'N/A' }}</h5>
                            <small>Last Received</small>
                        </b-card>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="12">
                        <h6>Complete Received History</h6>
                        <div style="max-height: 400px; overflow-y: auto;">
                            <b-table striped hover small :items="allReceivedEvents" :fields="eventFields">
                                <template #cell(date)="data">
                                    {{ moment(data.value).format('YYYY-MM-DD HH:mm') }}
                                </template>
                                <template #cell(order_type)="data">{{ data.value }}</template>
                                <template #cell(quantity)="data">{{ data.value }}</template>
                                <template #cell(created_by)="data">
                                    <span class="small">{{ data.value || 'System' }}</span>
                                </template>
                                <template #cell(action_type)="data">
                                    <b-badge :variant="data.value === 'create' ? 'success' : 'warning'" pill>
                                        {{ data.value }}
                                    </b-badge>
                                </template>
                            </b-table>
                        </div>
                    </b-col>
                </b-row>
            </div>
            <div v-else class="text-center py-5">
                <b-icon icon="inbox" font-scale="3" variant="secondary"></b-icon>
                <h5 class="mt-3">No received history found</h5>
                <p class="text-muted">No items have been received for this product yet</p>
            </div>
            <template #modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('modal-received-details')">Close</b-button>
            </template>
        </b-modal>

        <!-- Scheduled History Modal -->
        <b-modal content-class="pl-modal" dialog-class="pl-dialog" id="modal-scheduled-details"
                scrollable
                :title="`SCHEDULED HISTORY - ${currentProduct ? currentProduct.product_name : ''} (${currentProduct ? currentProduct.sku : ''})`"
                size="xl"
                hide-footer>
            <div v-if="isLoadingScheduledDetails" class="text-center py-5">
                <b-spinner variant="primary"></b-spinner>
                <p class="mt-2">Loading scheduled history...</p>
            </div>
            <div v-else-if="scheduledHistory && scheduledHistory.length > 0">
                <b-row class="mb-3">
                    <b-col md="3">
                        <b-card class="text-center" bg-variant="light">
                            <h5>{{ scheduledStats.total_scheduled }}</h5>
                            <small>Total Scheduled</small>
                        </b-card>
                    </b-col>
                    <b-col md="3">
                        <b-card class="text-center" bg-variant="light">
                            <h5>{{ scheduledStats.total_quantity }}</h5>
                            <small>Total Quantity</small>
                        </b-card>
                    </b-col>
                    <b-col md="3">
                        <b-card class="text-center" bg-variant="light">
                            <h5>{{ Object.keys(scheduledStats.by_status).join(', ') }}</h5>
                            <small>Status Distribution</small>
                        </b-card>
                    </b-col>
                    <b-col md="3">
                        <b-card class="text-center" bg-variant="light">
                            <h5>{{ scheduledHistory[0] && scheduledHistory[0].timeline && scheduledHistory[0].timeline.length > 0 ? moment(scheduledHistory[0].timeline[0].date).fromNow() : 'N/A' }}</h5>
                            <small>Last Scheduled</small>
                        </b-card>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="12">
                        <h6>Complete Scheduled History</h6>
                        <div style="max-height: 400px; overflow-y: auto;">
                            <b-table striped hover small :items="allScheduledEvents" :fields="eventFields">
                                <template #cell(date)="data">
                                    {{ moment(data.value).format('YYYY-MM-DD HH:mm') }}
                                </template>
                                <template #cell(order_type)="data">{{ data.value }}</template>
                                <template #cell(status)="data">{{ data.value }}</template>
                                <template #cell(quantity)="data">{{ data.value }}</template>
                                <template #cell(created_by)="data">
                                    <span class="small">{{ data.value || 'System' }}</span>
                                </template>
                                <template #cell(action_type)="data">
                                    <b-badge :variant="data.value === 'create' ? 'success' : 'warning'" pill>
                                        {{ data.value }}
                                    </b-badge>
                                </template>
                            </b-table>
                        </div>
                    </b-col>
                </b-row>
            </div>
            <div v-else class="text-center py-5">
                <b-icon icon="inbox" font-scale="3" variant="secondary"></b-icon>
                <h5 class="mt-3">No scheduled history found</h5>
                <p class="text-muted">No schedules have been created for this product yet</p>
            </div>
            <template #modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('modal-scheduled-details')">Close</b-button>
            </template>
        </b-modal>

        <!-- Stock Breakdown Drill-down Modal -->
        <StockBreakdownModal
            modal-id="modal-stock-breakdown"
            :product-sku="stockModalSku || ''"
            :product-name="stockModalName || ''"
            :location-id="location_id"
            :initial-tab="stockModalTab"
            @closed="closeStockBreakdown"
        />

        <!-- ============================================== -->
        <!-- Main Content -->
        <!-- ============================================== -->
        <div v-if="items_being_loaded == true">
            <loading :active="items_being_loaded" :can-cancel="false" :is-full-page="true"></loading>
        </div>

        <div v-else>
            <!-- TOOLBAR -->
            <div class="compact-toolbar">
                <div class="toolbar-group">
                    <b-button variant="outline-success" @click="fetch_init(false, true)" v-b-tooltip.hover.top="'Refresh Products'" size="sm">
                        <b-icon icon="arrow-repeat"></b-icon>
                        <span class="toolbar-label">REFRESH</span>
                    </b-button>
                </div>

                <div class="toolbar-group">
                    <b-button :variant="Showimage ? 'primary' : 'outline-secondary'" @click="toggleimage" v-b-tooltip.hover.top="'Show/Hide Image Column'" size="sm">
                        <b-icon :icon="Showimage ? 'eye' : 'eye-slash'"></b-icon>
                        <span class="toolbar-label">IMAGE</span>
                    </b-button>

                    <b-button :variant="ShowBarcode ? 'primary' : 'outline-secondary'" @click="toggleBarcode" v-b-tooltip.hover.top="'Show/Hide Barcode Column'" size="sm">
                        <b-icon :icon="ShowBarcode ? 'eye' : 'eye-slash'"></b-icon>
                        <span class="toolbar-label">BARCODE</span>
                    </b-button>

                    <b-button :variant="showMainColumns ? 'primary' : 'outline-secondary'" @click="toggleMainColumns" v-b-tooltip.hover.top="'Show/Hide Main Columns'" size="sm">
                        <b-icon :icon="showMainColumns ? 'eye' : 'eye-slash'"></b-icon>
                        <span class="toolbar-label">MAIN</span>
                    </b-button>

                    <b-button :variant="showPriceColumns ? 'primary' : 'outline-secondary'" @click="togglePriceColumns" v-b-tooltip.hover.top="'Show/Hide Price Columns'" size="sm">
                        <b-icon :icon="showPriceColumns ? 'eye' : 'eye-slash'"></b-icon>
                        <span class="toolbar-label">PRICE</span>
                    </b-button>

                    <b-button :variant="showDiscountColumns ? 'primary' : 'outline-secondary'" @click="toggleDiscountColumns" v-b-tooltip.hover.top="'Show/Hide Discount Columns'" size="sm">
                        <b-icon :icon="showDiscountColumns ? 'eye' : 'eye-slash'"></b-icon>
                        <span class="toolbar-label">DISCOUNT</span>
                    </b-button>

                    <b-button :variant="showDetailColumns ? 'primary' : 'outline-secondary'" @click="toggleDetailColumns" v-b-tooltip.hover.top="'Show/Hide Detail Columns'" size="sm">
                        <b-icon :icon="showDetailColumns ? 'eye' : 'eye-slash'"></b-icon>
                        <span class="toolbar-label">DETAILS</span>
                    </b-button>
                </div>
            </div>

            <!-- Mobile Filters Toggle -->
            <div v-if="isMobile" class="mobile-filters-toggle mb-2">
                <b-button size="sm" variant="outline-primary" @click="filtersVisible = !filtersVisible" block>
                    <b-icon-funnel-fill class="mr-1" />
                    {{ filtersVisible ? "Hide Filters" : "Show Filters" }}
                    <b-badge v-if="activeFiltersCount > 0" variant="primary" class="ml-1">{{ activeFiltersCount }}</b-badge>
                </b-button>
            </div>

            <!-- Filters -->
            <b-collapse :visible="!isMobile || filtersVisible" style="width: 100%;">
                <b-card class="filter-card" no-body>
                    <div class="filter-primary p-3">
                        <b-row class="align-items-end g-2">
                            <b-col md="2" class="mb-2">
                                <label class="filter-label">VENDOR</label>
                                <b-form-select :options="packet.vendors" v-model="vendor_id" v-on:change="fetch_vendor" size="sm" class="filter-select"></b-form-select>
                            </b-col>
                            <b-col md="2" class="mb-2">
                                <label class="filter-label">CATEGORY</label>
                                <b-form-select :options="packet.categories" v-model="category_id" v-on:change="fetch_category" size="sm" class="filter-select"></b-form-select>
                            </b-col>
                            <b-col md="2" class="mb-2">
                                <label class="filter-label">SUB CATEGORY</label>
                                <b-form-select :options="subcategoryOptions" v-model="subcategory_id" @change="fetchSubcategory" size="sm" class="filter-select" :disabled="!category_id"></b-form-select>
                            </b-col>
                            <b-col md="3" class="mb-2">
                                <label class="filter-label">SEARCH</label>
                                <b-input-group size="sm" class="filter-search">
                                    <b-form-input v-model="product_query" @keyup.enter="fetch_product_name_sku" placeholder="Name or SKU..." class="filter-input"></b-form-input>
                                    <b-input-group-append>
                                        <b-button variant="outline-secondary" @click="fetch_product_name_sku" size="sm">
                                            <b-icon icon="search"></b-icon>
                                        </b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </b-col>
                            <b-col v-if="packet.userDetails.userrole === 'admin'" md="2" class="mb-2">
                                <label class="filter-label">LOCATION</label>
                                <b-form-select :options="packet.locations" v-model="location_id" v-on:change="fetch_location" size="sm" class="filter-select"></b-form-select>
                            </b-col>
                            <b-col md="1" class="mb-2 text-right">
                                <b-button variant="link" size="sm" @click="showMoreFilters = !showMoreFilters" class="filter-toggle-btn p-0">
                                    <b-icon :icon="showMoreFilters ? 'chevron-up' : 'chevron-down'"></b-icon>
                                    {{ showMoreFilters ? 'Less' : 'More' }}
                                </b-button>
                            </b-col>
                        </b-row>
                    </div>

                    <b-collapse v-model="showMoreFilters">
                        <div class="filter-more p-3 border-top">
                            <b-row class="align-items-end g-2">
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">ITEM TYPE</label>
                                    <b-form-select :options="itemTypeOptions" v-model="itemTypeFilter" @change="applyFilters" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">ITEM STATUS</label>
                                    <b-form-select :options="itemStatusOptions" v-model="itemStatusFilter" @change="applyItemStatusFilter" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">PRODUCT STATUS</label>
                                    <b-form-select :options="statusOptions" v-model="statusFilter" v-on:change="applyStatusFilter" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">DISCONTINUED</label>
                                    <b-form-select :options="discontinuedOptions" v-model="discontinuedFilter" @change="applyDiscontinuedFilter" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">NEW ARRIVAL</label>
                                    <b-form-select :options="newArrivalOptions" v-model="newArrivalFilter" @change="applyNewArrivalFilter" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">RECEIVED STATUS</label>
                                    <b-form-select :options="receivedStatusOptions" v-model="receivedStatusFilter" @change="applyReceivedStatusFilter" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">TYPE</label>
                                    <b-form-select :options="typeOptions" v-model="typeFilter" @change="applyDetailFilters" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">COLOR</label>
                                    <b-form-select :options="colorOptions" v-model="colorFilter" @change="applyDetailFilters" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">SIZE</label>
                                    <b-form-select :options="sizeOptions" v-model="sizeFilter" @change="applyDetailFilters" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                            </b-row>

                            <b-row class="align-items-end g-2 mt-2">
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">MATERIAL</label>
                                    <b-form-select :options="materialOptions" v-model="materialFilter" @change="applyDetailFilters" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">FUNCTIONALITY</label>
                                    <b-form-select :options="functionalityOptions" v-model="functionalityFilter" @change="applyDetailFilters" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">SPIFF</label>
                                    <b-form-select :options="spiffFilterOptions" v-model="spiffFilter" @change="applySpiffFilter" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                <b-col md="3" class="mb-2">
                                    <label class="filter-label">BEST SELLING</label>
                                    <div class="d-flex align-items-center">
                                        <b-form-select :options="bestSellingOptions" v-model="bestSellingFilter" @change="applyBestSellingFilter" size="sm" class="filter-select flex-grow-1"></b-form-select>
                                        <b-badge v-if="bestSellingFilter" variant="warning" class="ml-2 filter-badge">Essential</b-badge>
                                    </div>
                                </b-col>
                                <b-col md="3" class="mb-2">
                                    <label class="filter-label">SLOWEST SELLING</label>
                                    <div class="d-flex align-items-center">
                                        <b-form-select :options="slowestSellingOptions" v-model="slowestSellingFilter" @change="applySlowestSellingFilter" size="sm" class="filter-select flex-grow-1"></b-form-select>
                                        <b-badge v-if="slowestSellingFilter" variant="danger" class="ml-2 filter-badge">Essential</b-badge>
                                    </div>
                                </b-col>
                                <b-col md="2" class="mb-2">
                                    <label class="filter-label">DISCOUNT TYPE</label>
                                    <b-form-select :options="discountTypeOptions" v-model="discountFilter" @change="fetchDiscountedProducts" size="sm" class="filter-select"></b-form-select>
                                </b-col>
                                <b-col md="2" class="mb-2 text-right">
                                    <b-button variant="outline-secondary" size="sm" @click="resetAllFilters" class="reset-filters-btn">
                                        <b-icon icon="arrow-repeat"></b-icon>
                                        Reset
                                    </b-button>
                                </b-col>
                            </b-row>
                        </div>
                    </b-collapse>

                    <div v-if="activeFiltersCount > 0" class="filter-active-badges p-2 border-top bg-light">
                        <small class="text-muted mr-2">Active filters:</small>
                        <b-badge v-for="filter in getActiveFiltersList" :key="filter.key" variant="info" class="mr-1 mb-1 filter-badge-active">
                            {{ filter.label }}: {{ filter.value }}
                            <b-icon icon="x" size="xs" class="ml-1" style="cursor: pointer;" @click="removeFilter(filter.key)"></b-icon>
                        </b-badge>
                        <b-button variant="link" size="sm" class="p-0 ml-2" @click="clearAllFilters">Clear all</b-button>
                    </div>

                    <div class="filter-stats-actions p-2 border-top bg-light" v-if="showStatsButton">
                        <b-row class="align-items-center">
                            <b-col md="12" class="text-right">
                                <b-button variant="info" size="sm" @click="fetchCategoryStockStats" :disabled="isLoadingCategoryStats" class="px-4">
                                    <b-icon icon="bar-chart-fill" class="mr-2"></b-icon>
                                    <span v-if="isLoadingCategoryStats">
                                        <b-spinner small class="mr-1"></b-spinner>Loading...
                                    </span>
                                    <span v-else>Show Stats for Stock</span>
                                </b-button>
                            </b-col>
                        </b-row>
                    </div>
                </b-card>
            </b-collapse>

            <!-- Products List -->
            <div v-if="displayedItems.length > 0" class="diver pc-list mt-2" @scroll="handleScroll">

                <div class="pc-bar">
                    <div class="pc-bar-sort">
                        <span class="pc-bar-label"><span class="pc-ico"><b-icon icon="arrow-down-up"></b-icon></span> Sort by</span>
                        <select class="pc-bar-select" :value="activeSortKey || ''" @change="onSortSelect($event.target.value)" aria-label="Sort products by">
                            <option value="" disabled>Choose a field</option>
                            <option v-for="opt in sortOptions" :key="opt.key" :value="opt.key">{{ opt.label }}</option>
                        </select>
                        <button v-if="activeSortKey" type="button" class="pc-bar-btn" @click="sortby(activeSortKey)"
                                :aria-label="activeSortAsc ? 'Sorted ascending, switch to descending' : 'Sorted descending, switch to ascending'">
                            <span class="pc-ico"><b-icon :icon="activeSortAsc ? 'arrow-up' : 'arrow-down'"></b-icon></span>
                            {{ activeSortAsc ? 'Ascending' : 'Descending' }}
                        </button>
                    </div>
                    <div class="pc-bar-view">
                        <span class="pc-bar-count">{{ filtered_final.length }} products</span>
                        <div class="pc-seg" role="group" aria-label="View">
                            <button :disabled="!isAdmin" type="button" :class="{ 'is-on': pcExpandAll }" @click="pcSetExpandAll(true)">
                                <span class="pc-ico"><b-icon icon="list-ul"></b-icon></span> Detailed
                            </button>
                            <button type="button" :class="{ 'is-on': !pcExpandAll }" @click="pcSetExpandAll(false)">
                                <span class="pc-ico"><b-icon icon="list"></b-icon></span> Compact
                            </button>
                        </div>
                    </div>
                </div>

                <div class="pc-head" :style="pcGridStyle">
                    <div v-if="pcShow.image" class="pc-head-cell"></div>
                    <div class="pc-head-cell">
                        <button type="button" :class="pcSortClass('product_name')" @click="sortby('product_name')">Product<b-icon v-if="activeSortKey === 'product_name'" :icon="activeSortAsc ? 'arrow-up' : 'arrow-down'"></b-icon></button>
                        <button type="button" :class="pcSortClass('sku')" @click="sortby('sku')">SKU<b-icon v-if="activeSortKey === 'sku'" :icon="activeSortAsc ? 'arrow-up' : 'arrow-down'"></b-icon></button>
                    </div>
                    <div class="pc-head-cell">
                        <button type="button" :class="pcSortClass('category_id')" @click="sortby('category_id')">Category<b-icon v-if="activeSortKey === 'category_id'" :icon="activeSortAsc ? 'arrow-up' : 'arrow-down'"></b-icon></button>
                        <button type="button" :class="pcSortClass('vendor_id')" @click="sortby('vendor_id')">Vendor<b-icon v-if="activeSortKey === 'vendor_id'" :icon="activeSortAsc ? 'arrow-up' : 'arrow-down'"></b-icon></button>
                    </div>
                    <div class="pc-head-cell is-num">
                        <button type="button" :class="pcSortClass('selling_price')" @click="sortby('selling_price')">Price<b-icon v-if="activeSortKey === 'selling_price'" :icon="activeSortAsc ? 'arrow-up' : 'arrow-down'"></b-icon></button>
                    </div>
                    <div class="pc-head-cell is-num">
                        <button type="button" :class="pcSortClass('available')" @click="sortby('available')">Available<b-icon v-if="activeSortKey === 'available'" :icon="activeSortAsc ? 'arrow-up' : 'arrow-down'"></b-icon></button>
                    </div>
                    <div class="pc-head-cell is-num">
                        <button type="button" :class="pcSortClass('sold_so_far')" @click="sortby('sold_so_far')">Sold<b-icon v-if="activeSortKey === 'sold_so_far'" :icon="activeSortAsc ? 'arrow-up' : 'arrow-down'"></b-icon></button>
                    </div>
                    <div class="pc-head-cell"></div>
                </div>

                <div v-if="showCategoryStats && categoryStats.category_id" class="pc-stats">
                    <div class="pc-stats-title">
                        <span class="pc-ico"><b-icon icon="bar-chart-fill"></b-icon></span>
                        <div>
                            <span>Category totals</span>
                            <strong>{{ categoryStats.category_name }}</strong>
                        </div>
                    </div>
                    <div class="pc-stats-grid">
                        <div><span>In stock</span><b>{{ categoryStats.total_in_stock }}</b></div>
                        <div><span>Reserved</span><b>{{ categoryStats.total_reserved }}</b></div>
                        <div class="is-good"><span>Available</span><b>{{ categoryStats.total_available }}</b></div>
                        <div v-if="!showSimplifiedColumns"><span>Layawayed</span><b>{{ categoryStats.total_layawayed }}</b></div>
                        <div v-if="!showSimplifiedColumns" class="is-bad"><span>Missing</span><b>{{ categoryStats.total_missing }}</b></div>
                        <div><span>Sold so far</span><b>{{ categoryStats.total_sold_so_far }}</b></div>
                        <div><span>Ordered</span><b>{{ categoryStats.total_ordered }}</b></div>
                        <div><span>Scheduled</span><b>{{ categoryStats.total_scheduled }}</b></div>
                    </div>
                </div>

                <article v-for="(item, idx) in filtered_final" :key="item.sku + '-' + idx"
                         class="pc-card"
                         :class="[
                             'is-' + pcStockState(item),
                             { 'is-open': pcIsOpen(item), 'is-inactive': item.status && item.status !== 'active' }
                         ]">

                    <div class="pc-sum" :style="pcGridStyle">

                        <div v-if="pcShow.image" class="pc-img">
                            <button v-if="getProductImage(item) && !item._imgError" type="button" class="pc-img-btn" @click="showFullImage(item)" :aria-label="'View image of ' + (item.product_name || item.sku)">
                                <img :src="getProductImage(item)" :alt="item.product_name || item.sku" @error="$set(item, '_imgError', true)" />
                            </button>
                            <div v-else class="pc-img-empty"><span class="pc-ico"><b-icon icon="image"></b-icon></span></div>
                        </div>

                        <div class="pc-prod">
                            <div class="pc-name" :title="item.product_name">{{ item.product_name }}</div>
                            <div class="pc-prod-meta">
                                <span class="pc-sku">SKU <b>{{ item.sku }}</b></span>
                                <template v-if="ShowBarcode && !showSimplifiedColumns">
                                    <button v-if="item.barcode_image && !item._barcodeError" type="button" class="pc-barcode" @click="showFullBarcode(item)" :aria-label="'View barcode for ' + item.sku">
                                        <img :src="item.barcode_image" :alt="'Barcode for ' + item.sku" @error="$set(item, '_barcodeError', true)" />
                                    </button>
                                </template>
                            </div>
                            <div class="pc-badges">
                                <span v-if="item.is_parent" class="pc-badge is-dark">Group</span>
                                <span v-if="hasActiveClearance(item)" class="pc-badge is-danger">Clearance</span>
                                <span v-else-if="hasActiveSale(item)" class="pc-badge is-orange">On sale</span>
                                <span v-if="item.discontinued" class="pc-badge is-danger">Discontinued</span>
                                <span v-if="item.is_new_arrival" class="pc-badge is-good">New arrival</span>
                                <span v-if="item.recently_received" class="pc-badge is-info">Recently received</span>
                                <span v-if="item.status && item.status !== 'active'" class="pc-badge is-muted">Disabled</span>
                                <span v-if="isLowInventoryTriggered(item)" class="pc-badge is-warn">Low stock</span>
                            </div>
                        </div>

                        <div class="pc-sum-cell pc-sum-class">
                            <div class="pc-sum-k">Category</div>
                            <div class="pc-sum-v" :title="packet.categoriesdict[item.category_id]">
                                {{ packet.categoriesdict[item.category_id] || '—' }}
                                <span v-if="getSubcategoryLabel(item.subcategory_id)" class="pc-sum-sub">› {{ getSubcategoryLabel(item.subcategory_id) }}</span>
                            </div>
                            <div class="pc-sum-k">Vendor</div>
                            <div class="pc-sum-v" :title="packet.vendorsdict[item.vendor_id]">{{ packet.vendorsdict[item.vendor_id] || '—' }}</div>
                        </div>

                        <div class="pc-sum-cell is-num pc-sum-price">
                            <div class="pc-sum-k">{{ hasActiveDiscount(item) ? 'Sale price' : 'Price' }}</div>
                            <div class="pc-big is-price">${{ hasActiveDiscount(item) ? calculateDiscountedPrice(item) : item.selling_price }}</div>
                            <div v-if="hasActiveDiscount(item)" class="pc-sum-note">was <s>${{ item.selling_price }}</s></div>
                            <!-- <div class="pc-sum-note pc-sum-costs">
                                <span>FOB: <b>${{ formatPriceNum(item.fob_price) }}</b></span>
                                <span>Landed: <b>${{ formatPriceNum(item.landed_cost) }}</b></span>
                                <span>Whlsl: <b>${{ formatPriceNum(item.wholesale_price) }}</b></span>
                            </div> -->
                        </div>

                        <div class="pc-sum-cell is-num pc-sum-avail">
                            <div class="pc-sum-k">Available</div>
                            <div class="pc-big" :class="'is-' + pcStockState(item)">{{ item.available || 0 }}</div>
                            <div class="pc-sum-note">{{ item.in_stock || 0 }} in stock · {{ item.reserved || 0 }} reserved</div>
                        </div>

                        <div class="pc-sum-cell is-num pc-sum-sold">
                            <div class="pc-sum-k">Sold so far</div>
                            <div class="pc-big">{{ item.sold_so_far || 0 }}</div>
                            <div class="pc-sum-note">Received {{ item.last_received_date ? moment(item.last_received_date).format('MMM DD, YYYY') : 'never' }}</div>
                        </div>

                        <div class="pc-sum-actions">
                            <button :disabled="!isAdmin" type="button" class="pc-icon-btn pc-toggle" @click="pcToggle(item)"
                                    :aria-expanded="pcIsOpen(item) ? 'true' : 'false'"
                                    :aria-label="pcIsOpen(item) ? 'Hide details' : 'Show details'"
                                    v-b-tooltip.hover.top :title="pcIsOpen(item) ? 'Hide details' : 'Show details'">
                                <span class="pc-ico"><b-icon :icon="pcIsOpen(item) ? 'chevron-up' : 'chevron-down'"></b-icon></span>
                            </button>
                        </div>
                    </div>

                    <div v-if="pcIsOpen(item)" class="pc-details">

                        <section v-if="pcShow.main" class="pc-sec">
                            <h4 class="pc-sec-title">Main</h4>
                            <div v-if="!showSimplifiedColumns" class="pc-row">
                                <span class="pc-k">Cubes</span>
                                <span class="pc-v">{{ item.cubes || 0 }}</span>
                            </div>
                            <div class="pc-row">
                                <span class="pc-k">Group</span>
                                <span class="pc-v">{{ item.is_parent ? 'Yes' : 'No' }}</span>
                            </div>
                            <div class="pc-row">
                                <span class="pc-k">Category</span>
                                <span class="pc-v" :title="packet.categoriesdict[item.category_id]">{{ packet.categoriesdict[item.category_id] || '—' }}</span>
                            </div>
                            <div class="pc-row">
                                <span class="pc-k">Sub category</span>
                                <span class="pc-v" :title="getSubcategoryLabel(item.subcategory_id)">{{ getSubcategoryLabel(item.subcategory_id) || '—' }}</span>
                            </div>
                            <div class="pc-row">
                                <span class="pc-k">Vendor</span>
                                <span class="pc-v" :title="packet.vendorsdict[item.vendor_id]">{{ packet.vendorsdict[item.vendor_id] || '—' }}</span>
                            </div>
                            <div v-if="!showSimplifiedColumns" class="pc-row">
                                <span class="pc-k">Qty / box</span>
                                <span class="pc-v">{{ item.quantity_per_box || (item.properties && item.properties.quantity_per_box) || 1 }}</span>
                            </div>
                            <div v-if="!showSimplifiedColumns" class="pc-row">
                                <span class="pc-k">Spiff</span>
                                <span class="pc-v" :class="{ 'is-good': parseFloat(item.spiff) > 0 }">{{ item.spiff || 0 }}</span>
                            </div>
                        </section>

                        <section v-if="pcShow.details" class="pc-sec">
                            <h4 class="pc-sec-title">Details</h4>
                            <div class="pc-row">
                                <span class="pc-k">Type</span>
                                <span class="pc-v">{{ item.type_name || item.type_code || 'N/A' }}</span>
                            </div>
                            <div class="pc-row">
                                <span class="pc-k">Color</span>
                                <span class="pc-v">{{ item.color_name || item.color_code || 'N/A' }}</span>
                            </div>
                            <div class="pc-row">
                                <span class="pc-k">Size</span>
                                <span class="pc-v">{{ item.size_name || item.size_code || 'N/A' }}</span>
                            </div>
                            <div class="pc-row">
                                <span class="pc-k">Material</span>
                                <span class="pc-v">{{ item.material_name || item.material_code || 'N/A' }}</span>
                            </div>
                            <div class="pc-row">
                                <span class="pc-k">Functionality</span>
                                <span class="pc-v">{{ item.functionality_name || item.functionality_code || 'N/A' }}</span>
                            </div>
                        </section>

                        <section class="pc-sec">
                            <h4 class="pc-sec-title">Pricing</h4>
                            <div class="pc-row">
                                <span class="pc-k">Price</span>
                                <span class="pc-v">${{ item.selling_price }}</span>
                            </div>
                            <template v-if="pcShow.costs">
                                <div class="pc-row">
                                    <span class="pc-k">FOB price</span>
                                    <span class="pc-v">${{ item.fob_price || 0 }}</span>
                                </div>
                                <div class="pc-row">
                                    <span class="pc-k">Landed price</span>
                                    <span class="pc-v">${{ item.landed_cost || 0 }}</span>
                                </div>
                                <div class="pc-row">
                                    <span class="pc-k">Wholesale</span>
                                    <span class="pc-v">${{ item.wholesale_price || 0 }}</span>
                                </div>
                                <div class="pc-row">
                                    <span class="pc-k">Profit margin</span>
                                    <span class="pc-v" :class="calculateProfitMargin(item.landed_cost, item.selling_price) >= 0 ? 'is-good' : 'is-bad'">
                                        {{ calculateProfitMargin(item.landed_cost, item.selling_price) }}%
                                    </span>
                                </div>
                            </template>
                        </section>

                        <section v-if="pcShow.sale" class="pc-sec">
                            <h4 class="pc-sec-title">Sale</h4>
                            <div class="pc-row">
                                <span class="pc-k">Sale type</span>
                                <span class="pc-v">
                                    <span v-if="hasActiveClearance(item)" class="pc-badge is-danger">Clearance</span>
                                    <span v-else-if="hasActiveSale(item)" class="pc-badge is-orange">Sale</span>
                                    <span v-else-if="item.sale_type" class="pc-badge is-muted">{{ item.sale_type }} ended</span>
                                    <template v-else>Regular</template>
                                </span>
                            </div>
                            <div class="pc-row">
                                <span class="pc-k">Special price</span>
                                <span class="pc-v" :class="{ 'is-good': hasActiveDiscount(item) }">
                                    {{ hasActiveDiscount(item) ? '$' + item.special_discount_price : '—' }}
                                </span>
                            </div>
                            <div class="pc-row">
                                <span class="pc-k">Discount ends</span>
                                <span class="pc-v">
                                    <template v-if="hasActiveDiscount(item)">{{ formatDate(item.special_discount_end_date) }}</template>
                                    <span v-else-if="item.special_discount_end_date && isDiscountExpired(item)" class="pc-muted">Expired</span>
                                    <span v-else class="pc-muted">—</span>
                                </span>
                            </div>
                            <div v-if="hasActiveDiscount(item) && parseFloat(item.selling_price) > parseFloat(item.special_discount_price)" class="pc-row">
                                <span class="pc-k">Customer saves</span>
                                <span class="pc-v is-good">${{ (parseFloat(item.selling_price) - parseFloat(item.special_discount_price)).toFixed(2) }}</span>
                            </div>
                        </section>

                        <section class="pc-sec">
                            <h4 class="pc-sec-title">Inventory</h4>
                            <div class="pc-row"><span class="pc-k">In stock</span><span class="pc-v">{{ item.in_stock || 0 }}</span></div>
                            <div class="pc-row"><span class="pc-k">Reserved</span><span class="pc-v">{{ item.reserved || 0 }}</span></div>
                            <div class="pc-row"><span class="pc-k">Available</span><span class="pc-v" :class="(item.available || 0) > 0 ? 'is-good' : 'is-bad'">{{ item.available || 0 }}</span></div>
                            <div v-if="!showSimplifiedColumns" class="pc-row"><span class="pc-k">Layawayed</span><span class="pc-v">{{ item.layawayed || 0 }}</span></div>
                            <div v-if="!showSimplifiedColumns" class="pc-row"><span class="pc-k">Missing</span><span class="pc-v" :class="{ 'is-bad': (item.missing || 0) > 0 }">{{ item.missing || 0 }}</span></div>
                        </section>

                        <section class="pc-sec">
                            <h4 class="pc-sec-title">History <small>tap for details</small></h4>
                            <button type="button" class="pc-row pc-row--btn" @click="show_sold_details(item)" :title="`Sold: ${item.sold_so_far}\nClick for time breakdown`">
                                <span class="pc-k">Sold so far</span>
                                <span class="pc-v">{{ item.sold_so_far || 0 }}</span>
                            </button>
                            <button type="button" class="pc-row pc-row--btn" @click="showOrderedDetails(item)">
                                <span class="pc-k">Last ordered</span>
                                <span class="pc-v">{{ item.ordered_from_history || 0 }} <small>{{ item.last_order_date ? moment(item.last_order_date).format('MMM DD, YYYY') : 'never' }}</small></span>
                            </button>
                            <button type="button" class="pc-row pc-row--btn" @click="showScheduledDetails(item)">
                                <span class="pc-k">Last scheduled</span>
                                <span class="pc-v">{{ item.scheduled_from_history || 0 }} <small>{{ item.last_scheduled_date ? moment(item.last_scheduled_date).format('MMM DD, YYYY') : 'never' }}</small></span>
                            </button>
                            <button type="button" class="pc-row pc-row--btn" @click="showReceivedDetails(item)">
                                <span class="pc-k">Last received</span>
                                <span class="pc-v">{{ item.received_from_history || 0 }} <small>{{ item.last_received_date ? moment(item.last_received_date).format('MMM DD, YYYY') : 'never' }}</small></span>
                            </button>
                        </section>
                    </div>
                </article>

                <div v-if="isLoadingMore" class="pc-loadmore">
                    <b-spinner small></b-spinner>
                    <span>Loading more products…</span>
                </div>
                <div v-if="!hasMore && displayedItems.length > 0" class="pc-end">
                    All {{ totalItemsCount }} products loaded
                </div>
            </div>

            <div v-else-if="!items_being_loaded">
                <b-alert variant="warning" show>No products to show</b-alert>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import global_vars from "@/utilities/global_vars";
import Requestor from "@/utilities/Requestor";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import ProductStatsModal from "@/components/Products/ProductStatsModal";
import StockBreakdownModal from "@/components/Products/StockBreakdownModal";

export default {
    name: "ProductManagerList",
    components: { Loading, DatePicker, ProductStatsModal, StockBreakdownModal },
    data: function() {
        return {
            packet: {},
            showMoreFilters: false,
            showMainColumns: true,
            showPriceColumns: true,
            showDiscountColumns: true,
            showSaleColumns: true,
            showStockColumns: true,
            user_location_ids: [],
multiple_locations_enabled: false,
            discountFilter: 'active',
            discountTypeOptions: [
                { value: 'active', text: 'ACTIVE DISCOUNTS' },
                { value: 'expired', text: 'EXPIRED DISCOUNTS' },
                { value: 'all', text: 'ALL DISCOUNTS' }
            ],
            isShowingDiscounted: false,
            ShowBarcode: true,
            orderHistory: [],
            orderStats: { total_orders: 0, total_quantity: 0, by_status: {}, by_type: {}, by_date: {} },
            moment: moment,
            scheduledHistory: [],
            scheduledStats: { total_scheduled: 0, total_quantity: 0, by_status: {}, by_type: {}, by_date: {} },
            receivedHistory: [],
            receivedStats: { total_received: 0, total_quantity: 0, by_status: {}, by_type: {}, by_date: {} },
            isLoadingReceivedDetails: false,
            isLoadingScheduledDetails: false,
            isLoadingOrderDetails: false,
            currentBarcodeImage: null,
            currentProductSku: null,
            Showimage: true,
            showDetailColumns: true,
            typeFilter: null,
            colorFilter: null,
            sizeFilter: null,
            materialFilter: null,
            functionalityFilter: null,
            typeOptions: [],
            colorOptions: [],
            sizeOptions: [],
            materialOptions: [],
            functionalityOptions: [],
            itemStatusFilter: null,
            itemStatusOptions: [{ value: null, text: 'ALL ITEM STATUSES' }, { value: 'in_stock', text: 'IN STOCK' }, { value: 'out_of_stock', text: 'OUT OF STOCK' }, { value: 'new_product', text: 'NEW PRODUCT' }, { value: 'ordered_product', text: 'ORDERED PRODUCT' }],
            allItems: [],
            displayedItems: [],
            currentOffset: 0,
            subcategory_id: null,
            subcategories: [],
            pageSize: 100,
            isLoadingMore: false,
            hasMore: true,
            totalItemsCount: 0,
            currentFullSizeImage: null,
            currentProductName: null,
            itemTypeFilter: null,
            itemTypeOptions: [{ value: null, text: 'ALL ITEM TYPES' }, { value: 'single', text: 'SINGLE ITEMS ONLY' }, { value: 'group', text: 'GROUP ITEMS ONLY' }],
            vendor_id: null,
            category_id: null,
            location_id: null,
            items_being_loaded: false,
            global_vars: global_vars,
            sort_asc: true,
            activeSortKey: null,
            activeSortAsc: true,
            pcExpandAll: false,
            product_query: null,
            limit: 100,
            currentProduct: null,
            detailFields: [{ key: 'period', label: 'Time Period', sortable: true }, { key: 'value', label: 'Quantity', sortable: true }],
            statusFilter: 'active',
            statusOptions: [{ value: 'all', text: 'ALL STATUSES' }, { value: 'active', text: 'ACTIVE ONLY' }, { value: 'notactive', text: 'DISABLED ONLY' }],
            bestSellingFilter: null,
            bestSellingOptions: [{ value: null, text: 'ALL PRODUCTS' }, { value: '7_days', text: 'BEST SELLERS FOR THE WEEK' }, { value: '30_days', text: 'BEST SELLERS FOR THE MONTH' }, { value: '60_days', text: 'BEST SELLERS FOR LAST 2 MONTHS' }, { value: '90_days', text: 'BEST SELLERS FOR LAST 3 MONTHS' }, { value: 'all_time', text: 'BEST SELLERS - ALL TIME' }, { value: 'profit_margin', text: 'HIGHEST PROFIT MARGIN %' }],
            slowestSellingFilter: null,
            slowestSellingOptions: [{ value: null, text: 'ALL PRODUCTS' }, { value: 'slowest_7_days', text: 'SLOWEST SELLERS FOR THE WEEK' }, { value: 'slowest_30_days', text: 'SLOWEST SELLERS FOR THE MONTH' }, { value: 'slowest_60_days', text: 'SLOWEST SELLERS FOR LAST 2 MONTHS' }, { value: 'slowest_90_days', text: 'SLOWEST SELLERS FOR LAST 3 MONTHS' }, { value: 'slowest_all_time', text: 'SLOWEST SELLERS - ALL TIME' }],
            bestSellingSortDesc: true,
            stats_item: null,
            user_location_id: null,
            user_location_name: "",
            locationsDict: {},
            searchTimeout: null,
            lastScrollTop: 0,
            filtersVisible: false,
            isMobile: false,
            showCategoryStats: false,
            isLoadingCategoryStats: false,
            categoryStats: {
                category_name: '', category_code: '', category_id: null,
                total_in_stock: 0, total_reserved: 0, total_available: 0, total_layawayed: 0, total_missing: 0,
                total_sold_so_far: 0, total_ordered: 0, total_scheduled: 0,
                total_products: 0, products_in_stock: 0, products_out_of_stock: 0, parent_products: 0, child_products: 0
            },
            discontinuedFilter: null,
            discontinuedOptions: [{ value: null, text: 'ALL' }, { value: 'active', text: 'ACTIVE PRODUCTS' }, { value: 'discontinued', text: 'DISCONTINUED' }],
            newArrivalFilter: null,
            newArrivalOptions: [{ value: null, text: 'ALL' }, { value: 'new', text: 'NEW ARRIVALS' }, { value: 'expired', text: 'EXPIRED NEW ARRIVALS' }],
            spiffFilter: null,
            spiffFilterOptions: [
                { value: null, text: 'ALL ITEMS' },
                { value: 'with_spiff', text: 'WITH SPIFF (>0)' },
                { value: 'without_spiff', text: 'WITHOUT SPIFF (0)' },
                { value: 'high_spiff', text: 'HIGH SPIFF (>10)' }
            ],
            receivedStatusFilter: null,
            receivedStatusOptions: [
                { value: null, text: 'ALL' },
                { value: 'recently_received', text: 'RECENTLY RECEIVED' },
                { value: 'not_recently_received', text: 'NOT RECENTLY RECEIVED' }
            ],
            stockBreakdown: {},
            stockModalSku: null,
            stockModalName: null,
            stockModalTab: 'in_stock'
        };
    },
    computed: {
        isAdmin() {
    const ud = (this.packet && this.packet.userDetails) || {};
    return ud.userrole === 'admin';
},
        pcShow() {
            const simple = this.showSimplifiedColumns;
            const isAdmin = this.packet && this.packet.userDetails && this.packet.userDetails.userrole === 'admin';
            return {
                image: this.Showimage,
                main: this.showMainColumns || simple,
                details: this.showDetailColumns && !simple,
                costs: isAdmin && this.showPriceColumns && !simple,
                sale: this.showDiscountColumns && !simple,
                actions: isAdmin,
            };
        },
        pcGridStyle() {
            const cols = [];
            if (this.pcShow.image) cols.push('150px');
            cols.push('minmax(0, 2.2fr)');
            cols.push('minmax(0, 1.5fr)');
            cols.push('minmax(0, 1.5fr)');
            cols.push('minmax(0, 1.1fr)');
            cols.push('minmax(0, 1.1fr)');
            cols.push('160px');
            return { '--pc-cols': cols.join(' ') };
        },
        sortOptions() {
            const s = this.pcShow;
            const opts = [
                { key: 'product_name', label: 'Product name' },
                { key: 'sku', label: 'SKU' },
                { key: 'selling_price', label: 'Price' },
                { key: 'in_stock', label: 'In stock' },
                { key: 'reserved', label: 'Reserved' },
                { key: 'available', label: 'Available' },
                { key: 'sold_so_far', label: 'Sold so far' },
                { key: 'ordered', label: 'Last ordered' },
                { key: 'scheduled', label: 'Last scheduled' },
                { key: 'received_from_history', label: 'Last received' },
            ];
            if (s.main) {
                opts.push(
                    { key: 'category_id', label: 'Category' },
                    { key: 'subcategory_id', label: 'Sub category' },
                    { key: 'vendor_id', label: 'Vendor' },
                    { key: 'is_parent', label: 'Group' }
                );
                if (!this.showSimplifiedColumns) {
                    opts.push({ key: 'cubes', label: 'Cubes' }, { key: 'quantity_per_box', label: 'Qty / box' });
                }
            }
            if (s.details) {
                opts.push(
                    { key: 'type_name', label: 'Type' },
                    { key: 'color_name', label: 'Color' },
                    { key: 'size_name', label: 'Size' },
                    { key: 'material_name', label: 'Material' },
                    { key: 'functionality_name', label: 'Functionality' }
                );
            }
            if (s.costs) {
                opts.push(
                    { key: 'fob_price', label: 'FOB price' },
                    { key: 'landed_cost', label: 'Landed price' },
                    { key: 'wholesale_price', label: 'Wholesale price' }
                );
            }
            return opts;
        },
        allReceivedEvents() {
            if (!this.receivedHistory || this.receivedHistory.length === 0) return [];
            const events = [];
            this.receivedHistory.forEach(order => {
                if (order.timeline && order.timeline.length > 0) {
                    order.timeline.forEach(event => {
                        events.push({
                            order_id: order.order_id,
                            order_type: order.order_type,
                            date: event.date,
                            quantity: event.quantity,
                            status: event.status || 'Received',
                            created_by: event.created_by,
                            action_type: event.action_type || 'create'
                        });
                    });
                }
            });
            return events.sort((a, b) => new Date(b.date) - new Date(a.date));
        },
        allScheduledEvents() {
            if (!this.scheduledHistory || this.scheduledHistory.length === 0) return [];
            const events = [];
            this.scheduledHistory.forEach(order => {
                if (order.timeline && order.timeline.length > 0) {
                    order.timeline.forEach(event => {
                        events.push({
                            order_id: order.order_id,
                            order_type: order.order_type,
                            date: event.date,
                            quantity: event.quantity,
                            status: event.status,
                            created_by: event.created_by,
                            action_type: event.action_type || 'create'
                        });
                    });
                }
            });
            return events.sort((a, b) => new Date(b.date) - new Date(a.date));
        },
        allOrderEvents() {
            const events = [];
            this.orderHistory.forEach(order => {
                order.timeline.forEach(event => {
                    events.push({
                        order_id: order.order_id,
                        order_type: order.order_type,
                        date: event.date,
                        quantity: event.quantity,
                        status: event.status,
                        created_by: event.created_by
                    });
                });
            });
            return events.sort((a, b) => new Date(b.date) - new Date(a.date));
        },
        eventFields() {
            return [
                { key: 'date', label: 'Date/Time', sortable: true },
                { key: 'order_id', label: 'Order ID', sortable: true },
                { key: 'order_type', label: 'Type', sortable: true },
                { key: 'quantity', label: 'Quantity', sortable: true },
                { key: 'status', label: 'Status', sortable: true },
                { key: 'created_by', label: 'By' }
            ];
        },
        activeFiltersCount() {
            let count = 0;
            if (this.vendor_id) count++;
            if (this.category_id) count++;
            if (this.subcategory_id) count++;
            if (this.product_query) count++;
            if (this.location_id && this.packet.userDetails.userrole === 'admin') count++;
            if (this.itemTypeFilter) count++;
            if (this.itemStatusFilter) count++;
            if (this.statusFilter !== 'all') count++;
            if (this.discontinuedFilter) count++;
            if (this.newArrivalFilter) count++;
            if (this.receivedStatusFilter) count++;
            if (this.typeFilter) count++;
            if (this.colorFilter) count++;
            if (this.sizeFilter) count++;
            if (this.materialFilter) count++;
            if (this.functionalityFilter) count++;
            if (this.spiffFilter) count++;
            if (this.bestSellingFilter) count++;
            if (this.slowestSellingFilter) count++;
            return count;
        },
        getActiveFiltersList() {
            const filters = [];
            if (this.vendor_id) filters.push({ key: 'vendor', label: 'Vendor', value: this.packet.vendorsdict[this.vendor_id] });
            if (this.category_id) filters.push({ key: 'category', label: 'Category', value: this.packet.categoriesdict[this.category_id] });
            if (this.subcategory_id) filters.push({ key: 'subcategory', label: 'Sub Category', value: this.getSubcategoryLabel(this.subcategory_id) });
            if (this.product_query) filters.push({ key: 'search', label: 'Search', value: this.product_query });
            if (this.location_id && this.packet.userDetails.userrole === 'admin') filters.push({ key: 'location', label: 'Location', value: this.locationsDict[this.location_id] });
            if (this.itemTypeFilter) filters.push({ key: 'itemType', label: 'Item Type', value: this.itemTypeFilter === 'single' ? 'Single' : 'Group' });
            if (this.itemStatusFilter) filters.push({ key: 'itemStatus', label: 'Item Status', value: this.itemStatusOptions.find(opt => opt.value === this.itemStatusFilter).text });
            if (this.statusFilter !== 'all') filters.push({ key: 'status', label: 'Status', value: this.statusFilter === 'active' ? 'Active' : 'Disabled' });
            if (this.discontinuedFilter) filters.push({ key: 'discontinued', label: 'Discontinued', value: this.discontinuedFilter === 'discontinued' ? 'Discontinued' : 'Active' });
            if (this.newArrivalFilter) filters.push({ key: 'newArrival', label: 'New Arrival', value: this.newArrivalFilter === 'new' ? 'New Arrivals' : 'Expired' });
            if (this.receivedStatusFilter) {
                const receivedLabels = { 'recently_received': 'Recently Received', 'not_recently_received': 'Not Recently Received' };
                filters.push({ key: 'receivedStatus', label: 'Received Status', value: receivedLabels[this.receivedStatusFilter] || this.receivedStatusFilter });
            }
            if (this.typeFilter) filters.push({ key: 'type', label: 'Type', value: this.typeOptions.find(opt => opt.value === this.typeFilter).text });
            if (this.colorFilter) filters.push({ key: 'color', label: 'Color', value: this.colorOptions.find(opt => opt.value === this.colorFilter).text });
            if (this.sizeFilter) filters.push({ key: 'size', label: 'Size', value: this.sizeOptions.find(opt => opt.value === this.sizeFilter).text });
            if (this.materialFilter) filters.push({ key: 'material', label: 'Material', value: this.materialOptions.find(opt => opt.value === this.materialFilter).text });
            if (this.functionalityFilter) filters.push({ key: 'functionality', label: 'Functionality', value: this.functionalityOptions.find(opt => opt.value === this.functionalityFilter).text });
            if (this.spiffFilter) {
                const spiffLabels = { 'with_spiff': 'With SPIFF (>0)', 'without_spiff': 'Without SPIFF (0)', 'high_spiff': 'High SPIFF (>10)' };
                filters.push({ key: 'spiff', label: 'SPIFF', value: spiffLabels[this.spiffFilter] || this.spiffFilter });
            }
            if (this.bestSellingFilter) filters.push({ key: 'bestSelling', label: 'Best Selling', value: this.bestSellingOptions.find(opt => opt.value === this.bestSellingFilter).text });
            if (this.slowestSellingFilter) filters.push({ key: 'slowestSelling', label: 'Slowest Selling', value: this.slowestSellingOptions.find(opt => opt.value === this.slowestSellingFilter).text });
            if (this.isShowingDiscounted) {
                const discountLabels = { 'active': 'Active Discounts', 'expired': 'Expired Discounts', 'all': 'All Discounts' };
                filters.push({ key: 'discount', label: 'Discount Type', value: discountLabels[this.discountFilter] || this.discountFilter });
            }
            return filters;
        },
        showSimplifiedColumns() {
            return this.bestSellingFilter || this.slowestSellingFilter;
        },
        subcategoryOptions() {
            if (!this.category_id) return [{ value: null, text: 'Select Category First' }];
            const filteredSubcategories = this.subcategories.filter(subcat => subcat.category_id == this.category_id);
            return [{ value: null, text: 'ALL' }, ...filteredSubcategories.map(subcat => ({ value: subcat.id, text: subcat.subcategory }))];
        },
        filtered_final() {
            if (!this.hasActiveFilters && this.displayedItems.length === 0) return [];
            let items = [...this.displayedItems];

            if (this.statusFilter !== 'all') {
                items = items.filter(item => {
                    let itemStatus = item.status;
                    let filterStatus = this.statusFilter;
                    if (filterStatus === 'notactive') return itemStatus === 'notactive' || itemStatus === 'not-active';
                    return itemStatus === filterStatus;
                });
            }
            if (this.discontinuedFilter) {
                items = items.filter(item => {
                    if (this.discontinuedFilter === 'discontinued') return item.discontinued === true;
                    else if (this.discontinuedFilter === 'active') return item.discontinued !== true;
                    return true;
                });
            }
            if (this.newArrivalFilter) {
                items = items.filter(item => {
                    if (this.newArrivalFilter === 'new') return item.is_new_arrival === true;
                    else if (this.newArrivalFilter === 'expired') return item.is_new_arrival !== true;
                    return true;
                });
            }
            if (this.receivedStatusFilter) {
                items = items.filter(item => {
                    if (this.receivedStatusFilter === 'recently_received') return item.recently_received === true;
                    else if (this.receivedStatusFilter === 'not_recently_received') return item.recently_received !== true;
                    return true;
                });
            }
            if (this.itemStatusFilter) {
                items = items.filter(item => {
                    const isOld = this.isOldProduct(item);
                    switch (this.itemStatusFilter) {
                        case 'in_stock': return item.in_stock > 0 && isOld;
                        case 'out_of_stock': return item.in_stock === 0 && isOld;
                        case 'new_product': return item.in_stock > 0 && !isOld;
                        case 'ordered_product': return item.ordered > 0 && item.in_stock === 0;
                        default: return true;
                    }
                });
            }
            if (this.itemTypeFilter === 'single') items = items.filter(item => !item.is_parent);
            else if (this.itemTypeFilter === 'group') items = items.filter(item => item.is_parent);

            if (this.spiffFilter) {
                items = items.filter(item => {
                    const spiff = parseFloat(item.spiff) || 0;
                    switch (this.spiffFilter) {
                        case 'with_spiff': return spiff > 0;
                        case 'without_spiff': return spiff === 0;
                        case 'high_spiff': return spiff > 10;
                        default: return true;
                    }
                });
            }
            if (this.bestSellingFilter) items = this.applyBestSellingSort(items);
            if (this.slowestSellingFilter) items = this.applySlowestSellingSort(items);

            if (!this.product_query) return items;
            else {
                let product_query = this.product_query.toUpperCase();
                return items.filter(d => ((d.product_name || '').toUpperCase().includes(product_query)) || ((d.sku || '').toUpperCase().includes(product_query)));
            }
        },
        hasActiveFilters() {
            return this.category_id !== null || this.vendor_id !== null || (this.packet.userDetails.userrole === 'admin' && this.location_id !== null) || this.subcategory_id !== null || this.itemTypeFilter !== null || this.itemStatusFilter !== null || this.statusFilter !== 'all' || this.discontinuedFilter !== null || this.newArrivalFilter !== null || this.receivedStatusFilter !== null || this.bestSellingFilter !== null || this.slowestSellingFilter !== null || this.spiffFilter !== null || this.hasActiveDetailFilters || (this.product_query && this.product_query.trim().length > 0);
        },
        hasActiveDetailFilters() {
            return this.typeFilter !== null || this.colorFilter !== null || this.sizeFilter !== null || this.materialFilter !== null || this.functionalityFilter !== null;
        },
        soldDetails() {
            if (!this.currentProduct) return [];
            return [
                { period: 'Total Sold', value: this.currentProduct.sold_so_far || 0 },
                { period: 'LAST 7 Days', value: this.currentProduct.sold_7d || 0 },
                { period: 'LAST 30 Days', value: this.currentProduct.sold_30d || 0 },
                { period: 'LAST 60 Days', value: this.currentProduct.sold_60d || 0 },
                { period: 'LAST 90 Days', value: this.currentProduct.sold_90d || 0 }
            ].filter(detail => detail.value > 0);
        },
        showStatsButton() {
            return this.category_id !== null && this.category_id !== '';
        }
    },
    watch: {
        product_query: function(newVal) {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                if (newVal && newVal.length >= 2) this.fetch_product_name_sku();
                else if (!newVal) {
                    this.currentOffset = 0;
                    this.hasMore = false;
                    this.allItems = [];
                    this.displayedItems = [];
                    this.updateDisplayedItems();
                }
            }, 5000);
        },
        category_id: function(newCategoryId) {
            this.showCategoryStats = false;
            if (newCategoryId) {
                this.fetchAllHierarchyOptions(newCategoryId);
                this.resetDetailFilters();
            } else {
                this.fetchAllHierarchyOptions();
            }
        }
    },
    methods: {
        // ============================================
        // STOCK BREAKDOWN
        // ============================================
        getStock(item) {
            const empty = {
                in_stock:       { store: 0, warehouse: 0, total: 0 },
                sold:           { store: 0, warehouse: 0, total: 0 },
                reserved:       { store: 0, warehouse: 0, total: 0 },
                layaway:        { store: 0, warehouse: 0, total: 0 },
                store_transfer: { store: 0, warehouse: 0, total: 0 },
                available:      { store: 0, warehouse: 0, total: 0 },
            };
            if (!item || !item.sku) return empty;
            const key = String(item.sku).toUpperCase();
            const b = this.stockBreakdown[key];
            if (b) return b;
            return {
                in_stock:       { store: 0, warehouse: 0, total: item.in_stock || 0 },
                sold:           { store: 0, warehouse: 0, total: 0 },
                reserved:       { store: 0, warehouse: 0, total: 0 },
                layaway:        { store: 0, warehouse: 0, total: item.layawayed || 0 },
                store_transfer: { store: 0, warehouse: 0, total: 0 },
                available:      { store: 0, warehouse: 0, total: item.available || 0 },
            };
        },

        async fetchStockBreakdown(skus) {
            if (!skus || skus.length === 0) return;
            const batchSize = 200;
            const batches = [];
            for (let i = 0; i < skus.length; i += batchSize) {
                batches.push(skus.slice(i, i + batchSize));
            }
            const merged = {};
            for (const batch of batches) {
                try {
                    const data = { skus: batch.join(','), limit: batch.length };
                    if (this.location_id) data.location_id = this.location_id;
                    const response = await new Promise((resolve, reject) => {
                        Requestor.getSyncP("/api/product/get_product_stock_breakdown_v3/", data, this, resolve, reject);
                    });
                    if (response.data && response.data.result) {
                        response.data.result.forEach(row => {
                            merged[row.product_sku] = {
                                in_stock:       row.in_stock       || { store: 0, warehouse: 0, total: 0 },
                                sold:           row.sold           || { store: 0, warehouse: 0, total: 0 },
                                reserved:       row.reserved       || { store: 0, warehouse: 0, total: 0 },
                                layaway:        row.layaway        || { store: 0, warehouse: 0, total: 0 },
                                store_transfer: row.store_transfer || { store: 0, warehouse: 0, total: 0 },
                                available:      row.available      || { store: 0, warehouse: 0, total: 0 },
                            };
                        });
                    }
                } catch (error) {
                    console.error("Error fetching stock breakdown batch:", error);
                }
            }
            this.stockBreakdown = { ...this.stockBreakdown, ...merged };
        },

        openStockBreakdown(item, tab = 'in_stock') {
            this.stockModalSku = null;
            this.stockModalTab = tab || 'in_stock';
            this.$nextTick(() => {
                this.stockModalSku = item.sku;
                this.stockModalName = item.product_name || item.sku;
                this.$bvModal.show('modal-stock-breakdown');
            });
        },

        closeStockBreakdown() {
            this.stockModalSku = null;
            this.stockModalName = null;
            this.stockModalTab = 'in_stock';
        },

        // ============================================
        // SALE / DISCOUNT HELPERS
        // ============================================
        parseSaleDate(d) {
            if (!d) return null;
            let m = moment(d, ['MM-DD-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
            if (!m.isValid()) m = moment(d);
            return m.isValid() ? m : null;
        },
        hasActiveDiscount(item) {
            if (!item || !(parseFloat(item.special_discount_price) > 0)) return false;
            const end = this.parseSaleDate(item.special_discount_end_date);
            if (!end) return false;
            return !moment().isAfter(end.clone().endOf('day'));
        },
        isDiscountExpired(item) {
            const end = this.parseSaleDate(item.special_discount_end_date);
            if (!end) return true;
            return moment().isAfter(end.clone().endOf('day'));
        },
        calculateDiscountedPrice(item) {
            if (!this.hasActiveDiscount(item)) return item.selling_price;
            return parseFloat(item.special_discount_price) || item.selling_price;
        },
        calculateDiscountPercentage(item) {
            if (!item.special_discount_price || !item.selling_price || item.selling_price <= 0) return 0;
            const d = ((parseFloat(item.selling_price) - parseFloat(item.special_discount_price)) / parseFloat(item.selling_price)) * 100;
            return Math.round(d);
        },
        hasActiveSale(item) { return item.sale_type === 'Sale' && this.hasActiveDiscount(item); },
        hasActiveClearance(item) { return item.sale_type === 'Clearance' && this.hasActiveDiscount(item); },
        formatDate(dateString) {
            const m = this.parseSaleDate(dateString);
            return m ? m.format('MMM D, YYYY') : '';
        },
        formatPriceNum(price) {
            if (!price && price !== 0) return '0.00';
            const num = parseFloat(price);
            return isNaN(num) ? '0.00' : num.toFixed(2);
        },
        calculateProfitMargin(landedCost, sellingPrice) {
            landedCost = parseFloat(landedCost);
            sellingPrice = parseFloat(sellingPrice);
            if (!landedCost || !sellingPrice || landedCost <= 0 || sellingPrice <= 0) return 0;
            const profit = sellingPrice - landedCost;
            return Math.round((profit / sellingPrice) * 100 * 10) / 10;
        },

        // ============================================
        // HISTORY MODALS
        // ============================================
        async showReceivedDetails(item) {
            if (!item || !item.sku) { this.$bvToast.toast("Invalid product", { title: "Error", variant: "danger" }); return; }
            this.currentProduct = item;
            this.isLoadingReceivedDetails = true;
            this.receivedHistory = [];
            this.receivedStats = { total_received: 0, total_quantity: 0, by_status: {}, by_type: {}, by_date: {} };
            try {
                const path = "/api/product/get_received_history/";
                const response = await new Promise((resolve, reject) => {
                    Requestor.getSyncP(path, { product_sku: item.sku, include_history: true }, this, resolve, reject);
                });
                if (response.data && response.data.success && response.data.result) {
                    if (response.data.result.length > 0) {
                        this.receivedHistory = this.processReceivedHistory(response.data.result);
                        this.receivedStats = this.calculateReceivedStats(response.data.result);
                    }
                    this.$bvModal.show('modal-received-details');
                } else if (response.data && response.data.error) throw new Error(response.data.error);
            } catch (error) {
                console.error('Error fetching received history:', error);
                this.$bvToast.toast(error.message || "Error loading received history", { title: "Error", variant: "danger" });
            } finally {
                this.isLoadingReceivedDetails = false;
            }
        },
        processReceivedHistory(historyData) {
            const groupedOrders = {};
            historyData.forEach(record => {
                const key = record.order_id + '_' + record.order_type;
                if (!groupedOrders[key]) {
                    groupedOrders[key] = { order_id: record.order_id, order_type: record.order_type, product_sku: record.product_sku, quantity: 0, timeline: [] };
                }
                groupedOrders[key].quantity += record.quantity || 0;
                groupedOrders[key].timeline.push({
                    date: record.order_date,
                    quantity: record.quantity || 0,
                    status: record.status_label || 'Received',
                    created_by: record.created_by,
                    version: record.version || 1,
                    action_type: record.action_type || 'create'
                });
            });
            return Object.values(groupedOrders);
        },
        calculateReceivedStats(historyData) {
            const stats = { total_received: 0, total_quantity: 0, by_status: {}, by_type: {}, by_date: {} };
            historyData.forEach(record => {
                stats.total_received++;
                stats.total_quantity += record.quantity || 0;
                stats.by_status[record.status_label || 'Received'] = (stats.by_status[record.status_label || 'Received'] || 0) + (record.quantity || 0);
                stats.by_type[record.order_type || 'Unknown'] = (stats.by_type[record.order_type || 'Unknown'] || 0) + (record.quantity || 0);
                if (record.order_date) {
                    const dateKey = moment(record.order_date).format('YYYY-MM-DD');
                    stats.by_date[dateKey] = (stats.by_date[dateKey] || 0) + (record.quantity || 0);
                }
            });
            return stats;
        },

        async showScheduledDetails(item) {
            if (!item || !item.sku) { this.$bvToast.toast("Invalid product", { title: "Error", variant: "danger" }); return; }
            this.currentProduct = item;
            this.isLoadingScheduledDetails = true;
            this.scheduledHistory = [];
            this.scheduledStats = { total_scheduled: 0, total_quantity: 0, by_status: {}, by_type: {}, by_date: {} };
            try {
                const response = await new Promise((resolve, reject) => {
                    Requestor.getSyncP("/api/product/get_scheduled_history/", { product_sku: item.sku, include_history: true }, this, resolve, reject);
                });
                if (response.data && response.data.success && response.data.result) {
                    if (response.data.result.length > 0) {
                        this.scheduledHistory = this.processScheduledHistory(response.data.result);
                        this.scheduledStats = this.calculateScheduledStats(response.data.result);
                    }
                    this.$bvModal.show('modal-scheduled-details');
                } else if (response.data && response.data.error) throw new Error(response.data.error);
            } catch (error) {
                console.error('Error fetching scheduled history:', error);
                this.$bvToast.toast(error.message || "Error loading scheduled history", { title: "Error", variant: "danger" });
            } finally {
                this.isLoadingScheduledDetails = false;
            }
        },
        processScheduledHistory(historyData) {
            const groupedOrders = {};
            historyData.forEach(record => {
                const key = record.order_id + '_' + record.order_type;
                if (!groupedOrders[key]) {
                    groupedOrders[key] = { order_id: record.order_id, order_type: record.order_type, product_sku: record.product_sku, quantity: 0, timeline: [] };
                }
                groupedOrders[key].quantity += record.quantity || 0;
                groupedOrders[key].timeline.push({
                    date: record.order_date,
                    quantity: record.quantity || 0,
                    status: record.status_label || 'Scheduled',
                    created_by: record.created_by,
                    version: record.version || 1,
                    action_type: record.action_type || 'create'
                });
            });
            return Object.values(groupedOrders);
        },
        calculateScheduledStats(historyData) {
            const stats = { total_scheduled: 0, total_quantity: 0, by_status: {}, by_type: {}, by_date: {} };
            historyData.forEach(record => {
                stats.total_scheduled++;
                stats.total_quantity += record.quantity || 0;
                stats.by_status[record.status_label || 'Scheduled'] = (stats.by_status[record.status_label || 'Scheduled'] || 0) + (record.quantity || 0);
                stats.by_type[record.order_type || 'Unknown'] = (stats.by_type[record.order_type || 'Unknown'] || 0) + (record.quantity || 0);
                if (record.order_date) {
                    const dateKey = moment(record.order_date).format('YYYY-MM-DD');
                    stats.by_date[dateKey] = (stats.by_date[dateKey] || 0) + (record.quantity || 0);
                }
            });
            return stats;
        },

        getStatusVariant(status) {
            const variants = { 'Ordered': 'success', 'Scheduled': 'warning', 'Pending': 'secondary', 'Shipped': 'info', 'Unknown': 'dark' };
            return variants[status] || 'secondary';
        },

        async showOrderedDetails(item) {
            this.currentProduct = item;
            this.isLoadingOrderDetails = true;
            try {
                const response = await new Promise((resolve, reject) => {
                    Requestor.getSyncP("/api/product/get_ordered_history/", { product_sku: item.sku, include_history: true }, this, resolve, reject);
                });
                if (response.data.result) {
                    this.orderHistory = this.processOrderHistory(response.data.result);
                    this.orderStats = this.calculateOrderStats(response.data.result);
                    this.$bvModal.show('modal-order-details');
                }
            } catch (error) {
                console.error('Error fetching order history:', error);
                this.$bvToast.toast("Error loading order history", { title: "Error", variant: "danger" });
            } finally {
                this.isLoadingOrderDetails = false;
            }
        },
        processOrderHistory(historyData) {
            const groupedOrders = {};
            historyData.forEach(record => {
                const key = record.order_id + '_' + record.order_type;
                if (!groupedOrders[key]) {
                    groupedOrders[key] = { order_id: record.order_id, order_type: record.order_type, product_sku: record.product_sku, quantity: 0, timeline: [] };
                }
                groupedOrders[key].quantity += record.quantity;
                groupedOrders[key].timeline.push({
                    date: record.order_date,
                    quantity: record.quantity,
                    status: record.status_label,
                    created_by: record.created_by,
                    version: record.version || 1
                });
            });
            return Object.values(groupedOrders);
        },
        calculateOrderStats(historyData) {
            const stats = { total_orders: 0, total_quantity: 0, by_status: {}, by_type: {}, by_date: {} };
            historyData.forEach(record => {
                stats.total_orders++;
                stats.total_quantity += record.quantity;
                stats.by_status[record.status_label] = (stats.by_status[record.status_label] || 0) + record.quantity;
                stats.by_type[record.order_type] = (stats.by_type[record.order_type] || 0) + record.quantity;
                const dateKey = moment(record.order_date).format('YYYY-MM-DD');
                stats.by_date[dateKey] = (stats.by_date[dateKey] || 0) + record.quantity;
            });
            return stats;
        },
        show_sold_details(item) { this.currentProduct = item; this.$bvModal.show('modal-sold-details'); },

        // ============================================
        // CATEGORY STATS
        // ============================================
        async fetchCategoryStockStats() {
            if (!this.category_id) { this.$bvToast.toast("Please select a category first", { title: "Warning", variant: "warning", autoHideDelay: 2000 }); return; }
            this.isLoadingCategoryStats = true;
            this.showCategoryStats = false;
            try {
                const data = { category_id: this.category_id, user_role: this.packet.userDetails.userrole, user_location_id: this.user_location_id };
                if (this.packet.userDetails.userrole === 'admin') data.location_id = this.location_id;
                const response = await new Promise((resolve, reject) => {
                    Requestor.getSyncP("/api/product/get_category_stock_stats/", data, this, resolve, reject);
                });
                if (response.data.error) {
                    this.$bvToast.toast(response.data.error, { title: "Error", variant: "danger", autoHideDelay: 3000 });
                } else {
                    const r = response.data.result;
                    this.categoryStats = {
                        category_name: r.category_name || '',
                        category_code: r.category_code || '',
                        category_id: r.category_id || this.category_id,
                        total_in_stock: r.total_in_stock || 0,
                        total_reserved: r.total_reserved || 0,
                        total_available: r.total_available || 0,
                        total_layawayed: r.total_layawayed || 0,
                        total_missing: r.total_missing || 0,
                        total_sold_so_far: r.total_sold_so_far || 0,
                        total_ordered: r.total_ordered || 0,
                        total_scheduled: r.total_scheduled || 0,
                        total_products: r.total_products || 0,
                        products_in_stock: r.products_in_stock || 0,
                        products_out_of_stock: r.products_out_of_stock || 0,
                        parent_products: r.parent_products || 0,
                        child_products: r.child_products || 0
                    };
                    this.showCategoryStats = true;
                    this.$bvToast.toast(`Stats loaded for ${this.categoryStats.category_name}`, { title: "Success", variant: "success", autoHideDelay: 2000 });
                }
            } catch (error) {
                console.error('Error fetching category stats:', error);
                this.$bvToast.toast("Error fetching category statistics", { title: "Error", variant: "danger", autoHideDelay: 3000 });
            } finally {
                this.isLoadingCategoryStats = false;
            }
        },

        // ============================================
        // FILTERS
        // ============================================
        resetAllFilters() {
            this.showMoreFilters = false;
            this.vendor_id = null;
            this.category_id = null;
            this.subcategory_id = null;
            this.product_query = null;
            this.itemTypeFilter = null;
            this.itemStatusFilter = null;
            this.statusFilter = 'all';
            this.discontinuedFilter = null;
            this.newArrivalFilter = null;
            this.receivedStatusFilter = null;
            this.typeFilter = null;
            this.colorFilter = null;
            this.sizeFilter = null;
            this.materialFilter = null;
            this.functionalityFilter = null;
            this.bestSellingFilter = null;
            this.slowestSellingFilter = null;
            this.spiffFilter = null;
            this.isShowingDiscounted = false;
            this.discountFilter = 'active';
            this.showCategoryStats = false;
            if (this.packet.userDetails.userrole === 'admin') this.location_id = null;
            else this.location_id = this.user_location_id;
            this.fetch_init(true, true);
        },
        removeFilter(filterKey) {
            switch (filterKey) {
                case 'vendor': this.vendor_id = null; break;
                case 'category': this.category_id = null; break;
                case 'subcategory': this.subcategory_id = null; break;
                case 'search': this.product_query = null; break;
                case 'location': this.location_id = this.user_location_id; break;
                case 'itemType': this.itemTypeFilter = null; break;
                case 'itemStatus': this.itemStatusFilter = null; break;
                case 'status': this.statusFilter = 'all'; break;
                case 'discontinued': this.discontinuedFilter = null; break;
                case 'newArrival': this.newArrivalFilter = null; break;
                case 'receivedStatus': this.receivedStatusFilter = null; break;
                case 'type': this.typeFilter = null; break;
                case 'color': this.colorFilter = null; break;
                case 'size': this.sizeFilter = null; break;
                case 'material': this.materialFilter = null; break;
                case 'functionality': this.functionalityFilter = null; break;
                case 'spiff': this.spiffFilter = null; break;
                case 'bestSelling': this.bestSellingFilter = null; break;
                case 'slowestSelling': this.slowestSellingFilter = null; break;
                case 'discount': this.isShowingDiscounted = false; this.discountFilter = 'active'; break;
            }
            this.showCategoryStats = false;
            this.fetch_init(false, true);
        },
        clearAllFilters() { this.resetAllFilters(); },

        // ============================================
        // BEST / SLOWEST SELLING
        // ============================================
      async fetchDiscountedProducts() {
    try {
        this.items_being_loaded = true;
        this.isShowingDiscounted = true;

        const path = "/api/product/get_discounted_products/";

        // ---- Re-read localStorage in case `this.packet` isn't populated yet ----
        let ud = (this.packet && this.packet.userDetails) || {};
        let udProps = ud.properties || {};
        let pk = this.packet || {};
        try {
            if (localStorage.userDetails) {
                const storedUd = JSON.parse(localStorage.userDetails);
                if (storedUd) {
                    ud = Object.assign({}, storedUd, ud);
                    udProps = Object.assign({}, storedUd.properties || {}, udProps);
                }
            }
            if (localStorage.packet) {
                const storedPk = JSON.parse(localStorage.packet);
                if (storedPk) pk = Object.assign({}, storedPk, pk);
            }
        } catch (e) {
            console.warn("[fetchDiscountedProducts] localStorage read failed:", e);
        }

        const pkProps = pk.properties || {};

        const pick = (...candidates) => {
            for (const c of candidates) {
                if (c === undefined || c === null || c === '') continue;
                const n = Number(c);
                if (!isNaN(n) && n > 0) return n;
            }
            return null;
        };

        // Resolve user location right here — no dependency on _continueInit / created
        const resolvedUserLocationId = pick(
            this.user_location_id,
            ud.location_id,
            ud.location,
            ud.locationId,
            udProps.location_id,
            udProps.location,
            pk.location_id,
            pk.location,
            pk.user_location_id,
            pk.selected_location,
            pkProps.location_id,
            pk.location_info && pk.location_info.location_id
        );

        const rawLocationIds =
            (ud && ud.location_ids) ||
            (udProps && udProps.location_ids) ||
            (this.user_location_ids && this.user_location_ids.length ? this.user_location_ids : null) ||
            pk.location_ids ||
            pkProps.location_ids ||
            (pk.location_info && pk.location_info.location_ids) ||
            [];
        const userLocationIds = Array.isArray(rawLocationIds)
            ? rawLocationIds.map(x => Number(x)).filter(x => !isNaN(x) && x > 0)
            : [];

        const isAdmin = ud.userrole === 'admin';

        // Fallback for non-admin with no resolver hit at all — bail loudly client-side
        if (!isAdmin && !resolvedUserLocationId && userLocationIds.length === 0) {
            console.error("[fetchDiscountedProducts] cannot resolve location.", { ud, udProps, pk, pkProps });
            this.$bvToast.toast(
                "Cannot resolve your location. Please re-login.",
                { title: "Error", variant: "danger", autoHideDelay: 4000 }
            );
            this.items_being_loaded = false;
            return;
        }

        const data = {
            discount_type: this.discountFilter || 'active',
            location_id: isAdmin
                ? this.location_id
                : (this.location_id || resolvedUserLocationId),
            user_role: ud.userrole || this.packet.userDetails.userrole,
            user_location_id: resolvedUserLocationId == null ? '' : String(resolvedUserLocationId),
            user_location_ids: userLocationIds,
            offset: 0,
            limit: this.pageSize
        };

        if (this.category_id) data.category_id = this.category_id;
        if (this.vendor_id) data.vendor_id = this.vendor_id;
        if (this.product_query) data.product_query = this.product_query;

        console.log("[fetchDiscountedProducts] sending:", data);

        const response = await new Promise((resolve, reject) => { Requestor.getSyncP(path, data, this, resolve, reject); });
        if (response.data.error) {
            this.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 2500, variant: "danger" });
        } else if (response.data.data) {
            this.allItems = response.data.data.map(item => ({
                ...item, edit: false,
                quantity_per_box: item.quantity_per_box || 1,
                wholesale_price: item.wholesale_price || 0,
                landed_cost: item.landed_cost || 0,
                spiff: item.spiff || 0,
                cubes: item.cubes || 0,
                in_stock: item.in_stock || 0,
                reserved: item.reserved || 0,
                available: item.available || 0,
                missing: Math.max(0, (item.in_stock || 0) - (item.reserved || 0) - (item.available || 0)),
                sale_type: item.sale_type || null,
                max_discount_percentage: item.max_discount_percentage || null
            }));
            this.updateDisplayedItems();
            this.totalItemsCount = this.allItems.length;
            this.hasMore = false;
            this.$bvToast.toast(`Found ${this.allItems.length} discounted products`, { title: "Success", autoHideDelay: 2000, variant: "success" });
            this.fetchStockBreakdown(this.allItems.map(i => i.sku));
        }
    } catch (error) {
        console.error('Error fetching discounted products:', error);
        this.$bvToast.toast("Error fetching discounted products", { title: "Error", autoHideDelay: 2000, variant: "danger" });
    } finally {
        this.items_being_loaded = false;
    }
},
        applySlowestSellingFilter() {
            if (!this.slowestSellingFilter) {
                this.currentOffset = 0; this.hasMore = false;
                this.allItems = []; this.displayedItems = [];
                this.updateDisplayedItems();
                return;
            }
            this.bestSellingFilter = null;
            this.items_being_loaded = true;
            const data = { time_period: this.slowestSellingFilter, limit: 1000, user_role: this.packet.userDetails.userrole, user_location_id: this.user_location_id };
            const that = this;
            Requestor.getSyncP("/api/product/get_slowest_selling_products/", data, this,
                function(response) {
                    that.items_being_loaded = false;
                    if (response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    else {
                        that.allItems = response.data.result.map(item => ({ ...item, edit: false, available: item.available || 0, in_stock: item.in_stock || 0, reserved: item.reserved || 0, missing: 0 }));
                        that.updateDisplayedItems();
                        that.totalItemsCount = that.allItems.length;
                        that.hasMore = false;
                        that.fetchStockBreakdown(that.allItems.map(i => i.sku));
                    }
                },
                function(error) { that.items_being_loaded = false; that.$bvToast.toast("Error fetching slowest selling", { title: "Error", autoHideDelay: 1500, variant: "danger" }); }
            );
        },
        applySlowestSellingSort(items) {
            const filter = this.slowestSellingFilter;
            let sortedItems = items.map(item => {
                let received = 0, sold = 0;
                switch (filter) {
                    case 'slowest_7_days': received = item.received_7d || 0; sold = item.sold_7d || 0; break;
                    case 'slowest_30_days': received = item.received_30d || 0; sold = item.sold_30d || 0; break;
                    case 'slowest_60_days': received = item.received_60d || 0; sold = item.sold_60d || 0; break;
                    case 'slowest_90_days': received = item.received_90d || 0; sold = item.sold_90d || 0; break;
                    case 'slowest_all_time': received = item.received || 0; sold = item.sold_so_far || 0; break;
                }
                let score = 0;
                if (received > 0) score = sold === 0 ? received * 1000 : received / sold;
                return { ...item, _slowScore: score };
            });
            return sortedItems.sort((a, b) => b._slowScore - a._slowScore);
        },

        applyBestSellingFilter() {
            if (!this.bestSellingFilter) {
                this.currentOffset = 0; this.hasMore = false;
                this.allItems = []; this.displayedItems = [];
                this.updateDisplayedItems();
                return;
            }
            this.slowestSellingFilter = null;
            this.items_being_loaded = true;
            const data = { time_period: this.bestSellingFilter, limit: 1000, user_role: this.packet.userDetails.userrole, user_location_id: this.user_location_id };
            const that = this;
            Requestor.getSyncP("/api/product/get_best_selling_products/", data, this,
                function(response) {
                    that.items_being_loaded = false;
                    if (response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    else {
                        that.allItems = response.data.result.map(item => ({ ...item, edit: false, available: item.available || 0, in_stock: item.in_stock || 0, reserved: item.reserved || 0, missing: 0 }));
                        that.updateDisplayedItems();
                        that.totalItemsCount = that.allItems.length;
                        that.hasMore = false;
                        that.fetchStockBreakdown(that.allItems.map(i => i.sku));
                    }
                },
                function(error) { that.items_being_loaded = false; that.$bvToast.toast("Error fetching best selling", { title: "Error", autoHideDelay: 1500, variant: "danger" }); }
            );
        },
        applyBestSellingSort(items) {
            const filter = this.bestSellingFilter;
            const desc = this.bestSellingSortDesc;
            const byField = (field) => (a, b) => desc ? (b[field] || 0) - (a[field] || 0) : (a[field] || 0) - (b[field] || 0);
            let sortedItems = [...items];
            switch (filter) {
                case '7_days': sortedItems.sort(byField('sold_7d')); break;
                case '30_days': sortedItems.sort(byField('sold_30d')); break;
                case '60_days': sortedItems.sort(byField('sold_60d')); break;
                case '90_days': sortedItems.sort(byField('sold_90d')); break;
                case 'all_time': sortedItems.sort(byField('sold_so_far')); break;
                case 'profit_margin': sortedItems.sort((a, b) => desc ? this.calculateProfitMargin(b.landed_cost, b.selling_price) - this.calculateProfitMargin(a.landed_cost, a.selling_price) : this.calculateProfitMargin(a.landed_cost, a.selling_price) - this.calculateProfitMargin(b.landed_cost, b.selling_price)); break;
            }
            return sortedItems;
        },

        // ============================================
        // PRODUCT LIST FETCH
        // ============================================
        async fetchProducts(offset = 0, limit = null, append = false) {
    if (this.isLoadingMore && append) return;
    this.isLoadingMore = true;
    if (!append) this.items_being_loaded = true;
    try {
        const path = "/api/product/get_products_list_v3/";
        const isAdmin = this.packet.userDetails.userrole === 'admin';

        // Always send what we resolved in _continueInit (or the admin dropdown value for admins).
        const resolvedUserLocationId =
            this.user_location_id ||
            this.packet.userDetails.location_id ||
            this.packet.userDetails.location ||
            this.packet.userDetails.locationId ||
            (this.packet.userDetails.properties && this.packet.userDetails.properties.location_id) ||
            (this.packet.location_info && this.packet.location_info.location_id) ||
            this.packet.location_id ||
            null;

        const data = {
            location_id: isAdmin
                ? this.location_id
                : (this.location_id || resolvedUserLocationId),
            user_role: this.packet.userDetails.userrole,
            user_location_id: resolvedUserLocationId == null ? '' : String(resolvedUserLocationId),
            user_location_ids: this.user_location_ids,
            offset: offset,
            limit: limit || this.pageSize
        };

        console.log("[fetchProducts] sending:", data);

        if (this.discontinuedFilter) data.discontinued = this.discontinuedFilter === 'discontinued';
        if (this.newArrivalFilter) data.is_new_arrival = this.newArrivalFilter === 'new';
        if (this.receivedStatusFilter === 'recently_received') data.recently_received = true;
        else if (this.receivedStatusFilter === 'not_recently_received') data.recently_received = false;
        if (this.typeFilter) data.type_id = this.typeFilter;
        if (this.colorFilter) data.color_id = this.colorFilter;
        if (this.sizeFilter) data.size_id = this.sizeFilter;
        if (this.materialFilter) data.material_id = this.materialFilter;
        if (this.functionalityFilter) data.functionality_id = this.functionalityFilter;
        if (this.itemTypeFilter === 'single') data.is_parent = false;
        else if (this.itemTypeFilter === 'group') data.is_parent = true;
        if (this.subcategory_id) data.subcategory_id = this.subcategory_id;
        if (this.category_id) data.category_id = this.category_id;
        if (this.vendor_id) data.vendor_id = this.vendor_id;
        if (this.product_query) data.product_query = this.product_query;
        if (this.statusFilter !== 'all') data.status = this.statusFilter;

        const response = await new Promise((resolve, reject) => { Requestor.getSyncP(path, data, this, resolve, reject); });
        if (response.data.error) {
            this.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 3000, variant: "danger" });
            this.allItems = [];
            this.displayedItems = [];
            this.totalItemsCount = 0;
            this.hasMore = false;
            return;
        }
        if (response.data.result) {
            const newItems = response.data.result.map(item => {
                const expectedAvailable = (item.in_stock || 0) - (item.reserved || 0);
                const missing = Math.max(0, expectedAvailable - (item.available || 0));
                return {
                    ...item, edit: false,
                    quantity_per_box: item.quantity_per_box || (item.properties && item.properties.quantity_per_box) || 1,
                    wholesale_price: item.wholesale_price || 0,
                    landed_cost: item.landed_cost || 0,
                    spiff: item.spiff || 0,
                    cubes: item.cubes || 0,
                    in_stock: item.in_stock || 0,
                    reserved: item.reserved || 0,
                    available: item.available || 0,
                    missing: missing,
                    sold_7d: item.sold_7d || 0,
                    sold_30d: item.sold_30d || 0,
                    sold_60d: item.sold_60d || 0,
                    received_7d: item.received_7d || 0,
                    received_30d: item.received_30d || 0,
                    received_60d: item.received_60d || 0,
                    scheduled_7d: item.scheduled_7d || 0,
                    scheduled_30d: item.scheduled_30d || 0,
                    scheduled_60d: item.scheduled_60d || 0,
                    special_discount_percent: item.special_discount_percent || 10,
                    special_discount_end_date: item.special_discount_end_date || null,
                    sale_type: item.sale_type || null,
                    max_discount_percentage: item.max_discount_percentage || null,
                    image_url: item.image_url || null,
                    discontinued: item.discontinued || false,
                    discontinued_date: item.discontinued_date || null,
                    is_new_arrival: item.is_new_arrival || false,
                    new_arrival_expiry_date: item.new_arrival_expiry_date || null,
                    recently_received: item.recently_received || false,
                };
            });
            if (append) this.allItems = [...this.allItems, ...newItems];
            else { this.allItems = newItems; this.totalItemsCount = newItems.length; }
            this.updateDisplayedItems();
            this.hasMore = newItems.length === this.pageSize;
            this.currentOffset = offset + newItems.length;
            this.totalItemsCount = this.allItems.length;
            this.fetchStockBreakdown(newItems.map(i => i.sku));
        }
    } catch (error) {
        console.error('Error fetching products:', error);
        this.$bvToast.toast("Error fetching products list", { title: "Error", autoHideDelay: 1500, variant: "danger" });
    } finally {
        this.items_being_loaded = false;
        this.isLoadingMore = false;
    }
},
        getSubcategoryLabel(subcategoryId) {
            if (!subcategoryId) return '';
            const subcategory = this.subcategories.find(sub => sub.id === subcategoryId);
            return subcategory ? subcategory.subcategory : subcategoryId;
        },
        updateDisplayedItems() { this.displayedItems = this.allItems; },
        async loadMore() {
            if (this.isLoadingMore || !this.hasMore) return;
            await this.fetchProducts(this.currentOffset, this.pageSize, true);
        },
        handleScroll(event) {
            const container = event.target;
            if (container.scrollTop === this.lastScrollTop) return;
            this.lastScrollTop = container.scrollTop;
            const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
            if (scrollBottom < 100 && !this.isLoadingMore && this.hasMore) this.loadMore();
        },
      async fetch_init(first, forceFetch = false) {
    if (first) {
        this.vendor_id = null;
        this.category_id = null;
        this.subcategory_id = null;
        this.product_query = null;
        this.bestSellingFilter = null;
        this.slowestSellingFilter = null;
        this.statusFilter = 'active';
        this.itemTypeFilter = null;
        this.itemStatusFilter = null;
        this.discontinuedFilter = null;
        this.newArrivalFilter = null;
        this.receivedStatusFilter = null;
        this.resetDetailFilters();
        if (this.packet.userDetails.userrole === 'admin') this.location_id = null;
        else this.location_id = this.user_location_id;
    }
    this.stockBreakdown = {};
    if (first || this.hasActiveFilters || forceFetch) {
        this.currentOffset = 0; this.hasMore = true;
        this.allItems = []; this.displayedItems = [];
        await this.fetchProducts(0, this.pageSize, false);
    } else {
        this.currentOffset = 0; this.hasMore = false;
        this.allItems = []; this.displayedItems = [];
        this.updateDisplayedItems();
    }
},

        // ============================================
        // FETCH TRIGGERS
        // ============================================
        async fetch_category() {
            this.resetDetailFilters();
            this.showCategoryStats = false;
            if (this.category_id) {
                this.subcategory_id = null;
                this.currentOffset = 0; this.hasMore = true;
                this.allItems = []; this.displayedItems = [];
                await this.fetchProducts(0, this.pageSize, false);
            } else await this.fetch_init(true);
        },
        async fetch_vendor() {
            if (this.vendor_id) {
                this.currentOffset = 0; this.hasMore = true;
                this.allItems = []; this.displayedItems = [];
                await this.fetchProducts(0, this.pageSize, false);
            } else await this.fetch_init(true);
        },
        applyStatusFilter() {
            if (this.statusFilter === 'not-active') this.statusFilter = 'notactive';
            if (this.statusFilter !== 'all') this.fetch_init(false, true);
            else this.fetch_init(true);
        },
        applySpiffFilter() {
            if (this.spiffFilter) {
                this.currentOffset = 0; this.hasMore = true;
                this.allItems = []; this.displayedItems = [];
                this.fetchProducts(0, this.pageSize, false);
            } else {
                this.currentOffset = 0; this.hasMore = false;
                this.allItems = []; this.displayedItems = [];
                this.updateDisplayedItems();
            }
        },
        applyReceivedStatusFilter() {
            if (this.receivedStatusFilter) {
                this.currentOffset = 0; this.hasMore = true;
                this.allItems = []; this.displayedItems = [];
                this.fetchProducts(0, this.pageSize, false);
            } else {
                this.currentOffset = 0; this.hasMore = false;
                this.allItems = []; this.displayedItems = [];
                this.updateDisplayedItems();
            }
        },
     async fetch_location() {
    if (this.packet.userDetails.userrole !== 'admin') {
        this.location_id = this.user_location_id;
        this.currentOffset = 0; this.hasMore = true;
        this.allItems = []; this.displayedItems = [];
        await this.fetchProducts(0, this.pageSize, false);
        return;
    }
    if (this.location_id) {
        this.currentOffset = 0; this.hasMore = true;
        this.allItems = []; this.displayedItems = [];
        await this.fetchProducts(0, this.pageSize, false);
    } else {
        this.currentOffset = 0; this.hasMore = false;
        this.allItems = []; this.displayedItems = [];
        this.updateDisplayedItems();
    }
},
        async fetch_product_name_sku() {
            clearTimeout(this.searchTimeout);
            if (this.product_query && this.product_query.length > 0) {
                this.currentOffset = 0; this.hasMore = true;
                this.allItems = []; this.displayedItems = [];
                await this.fetchProducts(0, this.pageSize, false);
            } else {
                this.currentOffset = 0; this.hasMore = false;
                this.allItems = []; this.displayedItems = [];
                this.updateDisplayedItems();
            }
        },
        applyItemStatusFilter() {
            this.currentOffset = 0; this.hasMore = true;
            this.allItems = []; this.displayedItems = [];
            this.fetchProducts(0, this.pageSize, false);
        },
        isOldProduct(item) {
            return (item.sold_so_far && item.sold_so_far > 0) || (item.received && item.received > 0) || (item.last_sale_date && moment().diff(moment(item.last_sale_date), 'days') > 30) || (item.created_at && moment().diff(moment(item.created_at), 'days') > 30) || (item.first_received_date && moment().diff(moment(item.first_received_date), 'days') > 30);
        },
        toggleimage() { this.Showimage = !this.Showimage; },
        toggleBarcode() { this.ShowBarcode = !this.ShowBarcode; },
        toggleMainColumns() { this.showMainColumns = !this.showMainColumns; },
        togglePriceColumns() { this.showPriceColumns = !this.showPriceColumns; },
        toggleDiscountColumns() { this.showDiscountColumns = !this.showDiscountColumns; },
        toggleDetailColumns() { this.showDetailColumns = !this.showDetailColumns; },
        applyDetailFilters() {
            this.currentOffset = 0; this.hasMore = true;
            this.allItems = []; this.displayedItems = [];
            this.fetchProducts(0, this.pageSize, false);
        },
        resetDetailFilters() {
            this.typeFilter = null; this.colorFilter = null; this.sizeFilter = null;
            this.materialFilter = null; this.functionalityFilter = null;
        },
        fetchAllHierarchyOptions(categoryId = null) {
            const data = categoryId ? { category_id: categoryId } : {};
            const that = this;
            Requestor.getSyncP("/api/product/get_hierarchy_options/", data, this,
                function(response) {
                    if (response.data && !response.data.error && response.data.result) {
                        const options = response.data.result;
                        that.typeOptions = [{ value: null, text: 'ALL TYPES' }, ...(options.types || []).map(type => ({ value: type.id, text: type.type_name || type.name || 'Unknown' }))];
                        that.colorOptions = [{ value: null, text: 'ALL COLORS' }, ...(options.colors || []).map(color => ({ value: color.id, text: color.color_name || color.name || 'Unknown' }))];
                        that.sizeOptions = [{ value: null, text: 'ALL SIZES' }, ...(options.sizes || []).map(size => ({ value: size.id, text: size.size_name || size.name || 'Unknown' }))];
                        that.materialOptions = [{ value: null, text: 'ALL MATERIALS' }, ...(options.materials || []).map(material => ({ value: material.id, text: material.material_name || material.name || 'Unknown' }))];
                        that.functionalityOptions = [{ value: null, text: 'ALL FUNCTIONALITIES' }, ...(options.functionalities || []).map(func => ({ value: func.id, text: func.functionality_name || func.name || 'Unknown' }))];
                        that.subcategories = options.subcategories || [];
                        that.packet.subcategoriesdict = that.subcategories.reduce((map, obj) => { map[obj.id] = obj.subcategory || obj.name; return map; }, {});
                    }
                },
                function(error) { console.error(error); }
            );
        },
        fetchSubcategory() {
            this.showCategoryStats = false;
            if (this.subcategory_id) {
                this.currentOffset = 0; this.hasMore = true;
                this.allItems = []; this.displayedItems = [];
                this.fetchProducts(0, this.pageSize, false);
            } else if (this.category_id) {
                this.fetch_category();
            } else {
                this.currentOffset = 0; this.hasMore = false;
                this.allItems = []; this.displayedItems = [];
                this.updateDisplayedItems();
            }
        },
        applyFilters() {
            if (this.itemTypeFilter !== null) this.fetch_init(false, true);
            else {
                this.currentOffset = 0; this.hasMore = false;
                this.allItems = []; this.displayedItems = [];
                this.updateDisplayedItems();
            }
        },
        applyNewArrivalFilter() {
            if (this.newArrivalFilter) {
                this.currentOffset = 0; this.hasMore = true;
                this.allItems = []; this.displayedItems = [];
                this.fetchProducts(0, this.pageSize, false);
            } else this.fetch_init(true);
        },
        applyDiscontinuedFilter() {
            if (this.discontinuedFilter) {
                this.currentOffset = 0; this.hasMore = true;
                this.allItems = []; this.displayedItems = [];
                this.fetchProducts(0, this.pageSize, false);
            } else this.fetch_init(true);
        },

        // ============================================
        // VIEW HELPERS
        // ============================================
        sortby(key) {
            if (key !== this.activeSortKey) this.sort_asc = true;
            const asc = this.sort_asc;
            if (asc) this.displayedItems = Requestor.sort(this.displayedItems, key);
            else this.displayedItems = Requestor.sort_reverse(this.displayedItems, key);
            this.sort_asc = !this.sort_asc;
            this.activeSortKey = key;
            this.activeSortAsc = asc;
        },
        onSortSelect(key) { if (!key) return; this.activeSortKey = null; this.sortby(key); },
        pcStockState(item) {
            const avail = parseFloat(item.available) || 0;
            if (avail <= 0) return 'out';
            if (this.isLowInventoryTriggered(item)) return 'low';
            return 'ok';
        },
        pcIsOpen(item) {
            if (item.edit == true) return true;
            return item._open !== undefined ? item._open : this.pcExpandAll;
        },
        pcToggle(item) { this.$set(item, '_open', !this.pcIsOpen(item)); },
        pcSetExpandAll(open) {
            this.pcExpandAll = open;
            (this.displayedItems || []).forEach(it => { if (it._open !== undefined) this.$delete(it, '_open'); });
        },
        pcSortClass(key) { return ['pc-head-sort', { 'is-active': this.activeSortKey === key }]; },
        show_item_stats(item) { this.stats_item = item; this.$bvModal.show("modal-product-stats"); },
        close_item_stats() { this.$bvModal.hide("modal-product-stats"); },
        monitor_modal_change(changed_product) {
            let item = this.allItems.filter(d => d.sku == changed_product.product_sku);
            if (item.length > 0) {
                item = item[0];
                this.allItems = this.allItems.map(u => u.id !== item.sku ? u : item);
            }
        },
        getProductImage(item) {
            if (!item || !item.image_url) return null;
            let imageUrl = item.image_url.trim();
            if (imageUrl === '' || imageUrl === 'null' || imageUrl === 'undefined') return null;
            if (imageUrl.includes('fmapi.myfurnituremecca.com')) return `/api/image/image-proxy/?url=${encodeURIComponent(imageUrl)}`;
            return imageUrl;
        },
        showFullImage(item) {
            if (!item) return;
            const imageUrl = this.getProductImage(item);
            if (imageUrl) {
                this.$bvModal.show('modal-image-viewer');
                this.$nextTick(() => {
                    this.currentFullSizeImage = imageUrl;
                    this.currentProductName = item.product_name || item.sku || 'Product Image';
                });
            } else this.$bvToast.toast("No image available", { title: "Info", autoHideDelay: 2000, variant: "info" });
        },
        showFullBarcode(item) {
            if (item.barcode_image) {
                this.$bvModal.show('modal-barcode-viewer');
                this.$nextTick(() => {
                    this.currentBarcodeImage = item.barcode_image;
                    this.currentProductName = item.product_name || item.sku;
                    this.currentProductSku = item.sku;
                });
            } else this.$bvToast.toast("No barcode available for this product", { title: "Info", autoHideDelay: 2000, variant: "info" });
        },
        downloadBarcodeImage(imageUrl, sku) {
            fetch(imageUrl).then(r => r.blob()).then(blob => {
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.href = url;
                link.download = `barcode_${sku}.png`;
                link.click();
                URL.revokeObjectURL(url);
            }).catch(error => console.error('Error downloading barcode:', error));
        },
        isLowInventoryTriggered(item) {
            if (item.is_low_inventory_triggered !== undefined) return item.is_low_inventory_triggered;
            const available = item.available || 0;
            const threshold = item.low_inventory_threshold || 5;
            const hasAlert = item.low_inventory_enabled || (item.properties && item.properties.low_inventory === true);
            return hasAlert && available <= threshold;
        },
        handleResize() { this.isMobile = window.innerWidth < 768; }
    },
    mounted() {
        this.handleResize();
        window.addEventListener("resize", this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.handleResize);
        clearTimeout(this.searchTimeout);
    },
  created() {
    this.packet = JSON.parse(localStorage.packet);
    this.packet.vendors.splice(0, 0, { value: null, text: "ALL" });
    this.packet.categories.splice(0, 0, { value: null, text: "ALL" });
    this.packet.userDetails = JSON.parse(localStorage.userDetails);
    this.typeOptions = [{ value: null, text: 'ALL TYPES' }];
    this.colorOptions = [{ value: null, text: 'ALL COLORS' }];
    this.sizeOptions = [{ value: null, text: 'ALL SIZES' }];
    this.materialOptions = [{ value: null, text: 'ALL MATERIALS' }];
    this.functionalityOptions = [{ value: null, text: 'ALL FUNCTIONALITIES' }];
    this.fetchAllHierarchyOptions();

    // Resolve location — the login response stores it at properties.location_id
    const ud = this.packet.userDetails || {};
    const udProps = ud.properties || {};

    this.user_location_id =
        ud.location_id ||
        ud.location ||
        ud.locationId ||
        udProps.location_id ||
        this.packet.location_id ||
        this.packet.selected_location ||
        (this.packet.location_info && this.packet.location_info.location_id) ||
        null;

    const rawLocationIds =
        ud.location_ids ||
        udProps.location_ids ||
        this.packet.location_ids ||
        (this.packet.location_info && this.packet.location_info.location_ids) ||
        [];
    this.user_location_ids = Array.isArray(rawLocationIds)
        ? rawLocationIds.map(x => Number(x)).filter(x => !isNaN(x) && x > 0)
        : [];

    this.multiple_locations_enabled =
        ud.multiple_locations_enabled === true ||
        udProps.multiple_locations_enabled === true;

    if (!this.user_location_id && this.user_location_ids.length > 0) {
        this.user_location_id = this.user_location_ids[0];
    }

    this.locationsDict = {};
    if (this.packet.locations && Array.isArray(this.packet.locations)) {
        this.packet.locations.forEach(loc => {
            const value = loc.value || loc.id || loc.location_id;
            const text = loc.text || loc.name || loc.location_name;
            this.locationsDict[value] = text;
        });
    }
    if (this.user_location_id && this.locationsDict[this.user_location_id]) {
        this.user_location_name = this.locationsDict[this.user_location_id];
    } else {
        this.user_location_name = "Default Location";
    }

    // Non-admin must have a location
    if (ud.userrole !== 'admin' && !this.user_location_id) {
        this.$bvToast.toast(
            "Your account has no location assigned. Please contact an administrator.",
            { title: "Access Error", variant: "danger", autoHideDelay: 6000 }
        );
        this.items_being_loaded = false;
        this.displayedItems = [];
        this.allItems = [];
        return;
    }

    // Pin non-admin to their assigned location
    if (ud.userrole !== 'admin') {
        this.location_id = this.user_location_id;
    }

    this.items_being_loaded = false;
    this.displayedItems = [];
    this.allItems = [];
    this.fetch_init(true);
}
};
</script>

<style scoped>
/* TOOLBAR */
.compact-toolbar {
    display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between;
    gap: 12px; background: #f8f9fa; padding: 8px 12px; border-radius: 8px;
    margin-bottom: 15px; border: 1px solid #e9ecef;
}
.toolbar-group { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; background: white; padding: 4px 10px; border-radius: 40px; border: 1px solid #e2e6ea; }
.toolbar-label { margin-left: 6px; font-size: 12px; font-weight: 500; }

/* FILTERS */
.filter-card { border: 1px solid #e8eaed; border-radius: 12px; background: #ffffff; overflow: hidden; margin-bottom: 1rem; }
.filter-primary { background: #ffffff; padding: 1.25rem 1.5rem; }
.filter-more { background: #fafbfc; border-top: 1px solid #eff2f5; padding: 1.25rem 1.5rem; }
.filter-label { font-size: 0.7rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: #5f6b7a; margin-bottom: 0.5rem; display: block; }
.filter-select, .filter-input { border-radius: 8px; border: 1px solid #e2e6ea; background: #ffffff; font-size: 0.875rem; color: #2c3e50; }
.filter-search { border-radius: 8px; overflow: hidden; }
.filter-search .form-control { border-right: none; border-radius: 8px 0 0 8px; }
.filter-search .btn { border-radius: 0 8px 8px 0; background: #ffffff; border-left: none; color: #8e9aab; }
.filter-toggle-btn { color: #7a8798; font-size: 0.75rem; font-weight: 500; padding: 0.375rem 0.75rem; border-radius: 20px; background: #f5f7fa; }
.filter-badge { font-size: 0.7rem; padding: 0.25rem 0.6rem; font-weight: 500; border-radius: 20px; }
.reset-filters-btn { border-radius: 8px; font-size: 0.75rem; padding: 0.375rem 1rem; border: 1px solid #e2e6ea; background: #ffffff; color: #5f6b7a; }
.filter-active-badges { background: #fefce8; border-top: 1px solid #fef3c7; padding: 0.75rem 1.5rem; font-size: 0.75rem; display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; }
.filter-badge-active { background: #fef3c7; color: #92400e; font-weight: 500; padding: 0.25rem 0.6rem; border-radius: 20px; font-size: 0.7rem; border: 1px solid #fde68a; }
.filter-stats-actions { background: #e8f4fd; border-top: 1px solid #b8d4e8; padding: 8px 15px; }

/* DIALOGS */
.diver { overflow: auto; width: 100%; height: 600px; border: 1px solid #dee2e6 !important; }
.details-container { padding: 15px; }

/* PRODUCTS LIST */
.pc-list {
  --pc-ink: #221F1C; --pc-ink-2: #34302B;
  --pc-orange: #EE7A22; --pc-orange-dark: #D4620F; --pc-orange-soft: #FFF1E4;
  --pc-teal: #0F6E6B; --pc-teal-soft: #E2F2F0;
  --pc-red: #C93C30; --pc-red-soft: #FBE9E7;
  --pc-warn: #9A6A0C; --pc-warn-soft: #FDF3DE;
  --pc-panel: #F7F5F2; --pc-line: #E8E3DB; --pc-muted: #736C63;
  --pc-head: 'Sora', 'Manrope', system-ui, sans-serif;
  font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: var(--pc-ink);
}
.diver.pc-list {
  height: calc(100vh - 230px); min-height: 520px;
  overflow-y: auto; overflow-x: hidden;
  border: 0 !important; background: transparent;
  padding: 0 2px 16px; -webkit-overflow-scrolling: touch;
}
.pc-ico { position: relative !important; display: inline-block !important; width: 1em !important; height: 1em !important; flex-shrink: 0; vertical-align: -0.125em; line-height: 1; }
.pc-ico >>> svg { position: absolute !important; top: 50% !important; left: 50% !important; width: 1em !important; height: 1em !important; margin: 0 !important; transform: translate(-50%, -50%) !important; display: block !important; }
.pc-list >>> svg.b-icon { width: 1em; height: 1em; vertical-align: -0.125em; }

.pc-bar { position: sticky; top: 0; z-index: 7; display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; padding: 8px 10px; margin-bottom: 8px; background: rgba(255, 255, 255, 0.97); border: 1px solid var(--pc-line); border-radius: 14px; }
.pc-bar-sort, .pc-bar-view { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.pc-bar-label { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 700; color: var(--pc-muted); }
.pc-bar-label .pc-ico { color: var(--pc-orange); }
.pc-bar-select { height: 36px; min-width: 190px; padding: 0 30px 0 12px; border: 1.5px solid var(--pc-line); border-radius: 10px; background-color: var(--pc-panel); font-size: 14px; font-weight: 700; color: var(--pc-ink); }
.pc-bar-btn { display: inline-flex; align-items: center; gap: 6px; height: 36px; padding: 0 12px; border: 1.5px solid var(--pc-line); border-radius: 10px; background: #fff; font-size: 13px; font-weight: 700; color: var(--pc-ink); cursor: pointer; }
.pc-bar-count { font-size: 13px; font-weight: 700; color: var(--pc-muted); }
.pc-seg { display: inline-flex; padding: 3px; border-radius: 10px; background: var(--pc-panel); border: 1px solid var(--pc-line); }
.pc-seg button { display: inline-flex; align-items: center; gap: 6px; height: 30px; padding: 0 12px; border: 0; border-radius: 8px; background: transparent; font-size: 13px; font-weight: 700; color: var(--pc-muted); cursor: pointer; }
.pc-seg button.is-on { background: var(--pc-ink); color: #fff; }

.pc-head { display: grid; grid-template-columns: var(--pc-cols); align-items: center; column-gap: 16px; padding: 0 15px 0 19px; margin-bottom: 8px; height: 36px; background: var(--pc-ink); border-radius: 12px; }
.pc-head-cell { display: flex; align-items: center; gap: 10px; min-width: 0; }
.pc-head-cell.is-num { justify-content: flex-start; }
.pc-head-sort { display: inline-flex; align-items: center; gap: 4px; padding: 0; border: 0; background: transparent; color: rgba(255, 255, 255, 0.72); font-size: 11.5px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; cursor: pointer; white-space: nowrap; }
.pc-head-sort:hover { color: #fff; }
.pc-head-sort.is-active { color: #FFB06B; }

.pc-stats { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; padding: 12px 14px; margin-bottom: 10px; background: var(--pc-ink); color: #fff; border-radius: 14px; }
.pc-stats-title { display: flex; align-items: center; gap: 10px; min-width: 200px; }
.pc-stats-title > .pc-ico { font-size: 20px; color: #FFB06B; }
.pc-stats-title span { display: block; font-size: 11px; font-weight: 700; color: rgba(255, 255, 255, 0.6); text-transform: uppercase; letter-spacing: 0.06em; }
.pc-stats-title strong { display: block; font-family: var(--pc-head); font-size: 16px; }
.pc-stats-grid { flex: 1; display: grid; grid-template-columns: repeat(auto-fit, minmax(104px, 1fr)); gap: 8px; }
.pc-stats-grid > div { background: rgba(255, 255, 255, 0.07); border-radius: 10px; padding: 7px 10px; }
.pc-stats-grid span { display: block; font-size: 12px; font-weight: 600; color: rgba(255, 255, 255, 0.62); }
.pc-stats-grid b { font-family: var(--pc-head); font-size: 18px; font-variant-numeric: tabular-nums; }
.pc-stats-grid .is-good b { color: #7FDACF; }
.pc-stats-grid .is-bad b { color: #FFB4A8; }

.pc-card { position: relative; margin-bottom: 10px; background: #fff; border: 1px solid var(--pc-line); border-radius: 14px; transition: border-color 0.15s ease, box-shadow 0.15s ease; }
.pc-card::before { content: ''; position: absolute; left: -1px; top: 14px; width: 4px; height: 56px; border-radius: 0 4px 4px 0; background: var(--pc-teal); }
.pc-card.is-low::before { background: #E0A21B; }
.pc-card.is-out::before { background: var(--pc-red); }
.pc-card:hover { border-color: #D9D1C5; box-shadow: 0 8px 22px -16px rgba(34, 31, 28, 0.4); }
.pc-card.is-inactive { background: #FBFAF8; }

.pc-sum { display: grid; grid-template-columns: var(--pc-cols); align-items: start; column-gap: 16px; padding: 12px 14px 12px 18px; }
.pc-img { position: relative; width: 150px; height: 84px; border-radius: 12px; background: linear-gradient(160deg, #F7F3EE, #EDE8E1); overflow: hidden; }
.pc-img-btn { position: absolute; inset: 0; width: 100%; height: 100%; padding: 6px; border: 0; background: transparent; cursor: zoom-in; }
.pc-img-btn img { width: 100%; height: 100%; object-fit: contain; display: block; }
.pc-img-empty { position: absolute; inset: 0; color: #B9B1A6; font-size: 28px; }
.pc-img-empty > .pc-ico { position: absolute !important; top: 50%; left: 50%; transform: translate(-50%, -50%); }

.pc-prod { min-width: 0; display: flex; flex-direction: column; gap: 5px; }
.pc-name { font-family: var(--pc-head); font-size: 15.5px; font-weight: 700; line-height: 1.3; color: var(--pc-ink); word-break: break-word; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.pc-prod-meta { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.pc-sku { font-size: 13px; font-weight: 600; color: var(--pc-muted); }
.pc-sku b { color: var(--pc-ink-2); font-weight: 700; }
.pc-barcode { height: 28px; padding: 2px 6px; border: 1px solid var(--pc-line); border-radius: 7px; background: #fff; cursor: zoom-in; }
.pc-barcode img { display: block; height: 100%; max-width: 150px; object-fit: contain; }
.pc-badges { display: flex; flex-wrap: wrap; gap: 5px; }
.pc-badges:empty { display: none; }
.pc-badge { display: inline-block; padding: 2px 9px; border-radius: 99px; font-size: 11.5px; font-weight: 800; line-height: 1.4; background: #fff; border: 1px solid var(--pc-line); color: var(--pc-ink); white-space: nowrap; }
.pc-badge.is-dark { background: var(--pc-ink); border-color: var(--pc-ink); color: #fff; }
.pc-badge.is-good { background: var(--pc-teal-soft); border-color: #BFE1DD; color: var(--pc-teal); }
.pc-badge.is-danger { background: var(--pc-red-soft); border-color: #F3C9C3; color: var(--pc-red); }
.pc-badge.is-info { background: #E2EFF7; border-color: #C3DCEC; color: #1F6690; }
.pc-badge.is-orange { background: var(--pc-orange-soft); border-color: #F5CFAE; color: var(--pc-orange-dark); }
.pc-badge.is-warn { background: var(--pc-warn-soft); border-color: #F1DDAE; color: var(--pc-warn); }
.pc-badge.is-muted { background: #EFECE7; color: var(--pc-muted); }

.pc-sum-cell { min-width: 0; }
.pc-sum-k { font-size: 11.5px; font-weight: 700; letter-spacing: 0.03em; color: var(--pc-muted); text-transform: uppercase; }
.pc-sum-v { font-size: 14px; font-weight: 700; color: var(--pc-ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 4px; }
.pc-sum-v:last-child { margin-bottom: 0; }
.pc-sum-sub { color: var(--pc-muted); font-weight: 600; }
.pc-big { font-family: var(--pc-head); font-size: 21px; font-weight: 800; line-height: 1.2; font-variant-numeric: tabular-nums; color: var(--pc-ink); }
.pc-big.is-price { color: var(--pc-orange-dark); }
.pc-big.is-ok { color: var(--pc-teal); }
.pc-big.is-low { color: #B07A06; }
.pc-big.is-out { color: var(--pc-red); }
.pc-sum-note { font-size: 12px; font-weight: 600; color: var(--pc-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pc-sum-costs { display: flex; flex-wrap: wrap; gap: 4px 10px; margin-top: 4px; font-size: 14px; color: var(--pc-muted); }
.pc-sum-costs span { white-space: nowrap; }
.pc-sum-costs b { color: var(--pc-ink-2); font-weight: 700; font-variant-numeric: tabular-nums; }

.pc-sum-actions { display: flex; align-items: center; justify-content: flex-end; gap: 4px; }
.pc-icon-btn { position: relative; width: 34px; height: 34px; padding: 0 !important; border: 1px solid var(--pc-line) !important; border-radius: 9px !important; background: #fff !important; color: var(--pc-ink-2) !important; font-size: 15px; box-shadow: none !important; cursor: pointer; }
.pc-icon-btn > .pc-ico { position: absolute !important; top: 50% !important; left: 50%; transform: translate(-50%, -50%); }
.pc-icon-btn:hover { border-color: var(--pc-orange) !important; color: var(--pc-orange-dark) !important; }
.pc-toggle { background: var(--pc-panel) !important; }
.pc-card.is-open .pc-toggle { background: var(--pc-ink) !important; border-color: var(--pc-ink) !important; color: #fff !important; }

.pc-details { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 10px; padding: 12px 14px 14px 18px; border-top: 1px solid var(--pc-line); background: #FCFBF9; border-radius: 0 0 14px 14px; }
.pc-sec { min-width: 0; padding: 10px 14px 6px; background: #fff; border: 1px solid var(--pc-line); border-radius: 12px; }
.pc-sec-title { display: flex; align-items: baseline; justify-content: space-between; margin: 0 0 4px; padding-bottom: 6px; border-bottom: 2px solid var(--pc-orange); font-family: var(--pc-head); font-size: 13px; font-weight: 700; color: var(--pc-ink); }
.pc-sec-title small { font-family: 'Manrope', sans-serif; font-size: 11px; font-weight: 600; color: var(--pc-muted); }
.pc-row { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; padding: 6px 0; border-bottom: 1px dashed var(--pc-line); font-size: 13.5px; }
.pc-row:last-child { border-bottom: 0; }
.pc-k { color: var(--pc-muted); font-weight: 600; white-space: nowrap; flex-shrink: 0; }
.pc-v { min-width: 0; font-weight: 700; color: var(--pc-ink); text-align: right; font-variant-numeric: tabular-nums; word-break: break-word; }
.pc-v small { display: block; font-size: 11.5px; font-weight: 600; color: var(--pc-orange-dark); }
.pc-muted { color: var(--pc-muted); font-weight: 600; }
.is-good { color: var(--pc-teal) !important; }
.is-bad { color: var(--pc-red) !important; }
.pc-row--btn { width: calc(100% + 12px); margin: 0 -6px; padding: 6px; border: 0; border-bottom: 1px dashed var(--pc-line); border-radius: 6px; background: transparent; text-align: left; cursor: pointer; }
.pc-row--btn:last-child { border-bottom: 0; }
.pc-row--btn:hover { background: var(--pc-orange-soft); }

.pc-loadmore { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 16px; font-weight: 700; color: var(--pc-muted); }
.pc-end { text-align: center; padding: 10px; font-size: 13px; font-weight: 700; color: var(--pc-muted); }

/* RESPONSIVE */
@media (max-width: 1199.98px) {
  .pc-head { display: none; }
  .pc-sum { grid-template-columns: auto minmax(0, 1fr) auto auto auto; column-gap: 14px; }
  .pc-sum-class, .pc-sum-sold { display: none; }
  .pc-sum-cell.is-num { text-align: right; }
  .pc-img { width: 76px; height: 76px; }
}
@media (max-width: 767.98px) {
  .diver.pc-list { height: calc(100vh - 150px); min-height: 420px; }
  .pc-bar { padding: 8px; }
  .pc-bar-sort, .pc-bar-view { width: 100%; }
  .pc-bar-select { flex: 1 1 160px; min-width: 0; font-size: 16px; }
  .pc-bar-view { justify-content: space-between; }
  .pc-sum {
    grid-template-columns: 64px repeat(3, minmax(0, 1fr));
    grid-template-areas:
      "img  prod  prod  prod"
      "price price avail sold"
      "act  act   act   act";
    gap: 10px;
    padding: 12px 12px 12px 16px;
  }
  .pc-img { grid-area: img; width: 64px; height: 64px; }
  .pc-prod { grid-area: prod; }
  .pc-name { font-size: 14.5px; }
  .pc-sum-price { grid-area: price; }
  .pc-sum-avail { grid-area: avail; }
  .pc-sum-sold { grid-area: sold; display: block !important; }
  .pc-sum-cell.is-num { text-align: left; padding: 8px 10px; border-radius: 10px; background: var(--pc-panel); }
  .pc-big { font-size: 18px; }
  .pc-sum-note { white-space: normal; }
  .pc-sum-actions { grid-area: act; justify-content: flex-end; }
  .pc-icon-btn { width: 42px; height: 40px; }
  .pc-details { padding: 10px; gap: 8px; }
}
@media (max-width: 399.98px) {
  .pc-sum { grid-template-columns: 56px minmax(0, 1fr) auto; }
  .pc-img { width: 56px; height: 56px; }
}
@media (pointer: coarse) {
  .pc-icon-btn { width: 42px; height: 40px; }
  .pc-row--btn { min-height: 40px; }
  .pc-bar-select, .pc-bar-btn { height: 42px; }
  .pc-seg button { height: 36px; }
}
@media (prefers-reduced-motion: reduce) {
  .pc-card { transition: none; }
}
</style>

<style>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Sora:wght@600;700;800&display=swap');

.pl-page, .pl-modal {
  --pl-ink: #221F1C; --pl-ink-2: #34302B;
  --pl-orange: #EE7A22; --pl-orange-dark: #D4620F; --pl-orange-soft: #FFF1E4;
  --pl-teal: #0F6E6B; --pl-teal-soft: #E2F2F0;
  --pl-red: #C93C30; --pl-red-soft: #FBE9E7;
  --pl-amber: #C98A12; --pl-amber-soft: #FDF3DE;
  --pl-line: #E5E1DA; --pl-mist: #F6F4F0; --pl-muted: #736C63;
  font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: var(--pl-ink);
}
.pl-page .compact-toolbar { background: var(--pl-ink) !important; border: 0 !important; border-radius: 16px !important; padding: 8px 10px !important; margin-bottom: 10px !important; gap: 8px !important; }
.pl-page .compact-toolbar .toolbar-group { background: rgba(255, 255, 255, 0.06) !important; border: 1px solid rgba(255, 255, 255, 0.12) !important; border-radius: 12px !important; padding: 3px 4px !important; gap: 2px !important; }
.pl-page .compact-toolbar .toolbar-group .btn, .pl-page .compact-toolbar .toolbar-group .b-dropdown > .btn { display: inline-flex !important; align-items: center; gap: 6px; min-height: 32px; padding: 5px 10px !important; border: 0 !important; border-radius: 9px !important; background: transparent !important; color: rgba(255, 255, 255, 0.86) !important; font-size: 12.5px !important; font-weight: 700 !important; box-shadow: none !important; }
.pl-page .compact-toolbar .toolbar-group .btn:hover:not(:disabled), .pl-page .compact-toolbar .toolbar-group .b-dropdown > .btn:hover { background: rgba(255, 255, 255, 0.1) !important; color: #fff !important; }
.pl-page .compact-toolbar .toolbar-group .btn-primary, .pl-page .compact-toolbar .toolbar-group .b-dropdown > .btn-primary { background: var(--pl-orange) !important; color: #fff !important; }
.pl-page .compact-toolbar .toolbar-group .btn-primary:hover { background: var(--pl-orange-dark) !important; }
.pl-page .compact-toolbar .toolbar-group .btn:disabled { opacity: 0.4 !important; }
.pl-page .compact-toolbar .badge { background: var(--pl-orange) !important; color: #fff !important; border-radius: 99px; }
.pl-page .btn { border-radius: 10px; font-weight: 700; }
.pl-page .btn-primary, .pl-page .btn-info { background: var(--pl-orange); border-color: var(--pl-orange); color: #fff; }
.pl-page .btn-primary:hover:not(:disabled), .pl-page .btn-info:hover:not(:disabled) { background: var(--pl-orange-dark); border-color: var(--pl-orange-dark); }
.pl-page .btn-success { background: var(--pl-teal); border-color: var(--pl-teal); }
.pl-page .btn-danger { background: var(--pl-red); border-color: var(--pl-red); }
.pl-page .btn-secondary { background: #fff; border-color: var(--pl-line); color: var(--pl-ink); }
.pl-page .filter-card { border: 1px solid var(--pl-line) !important; border-radius: 16px !important; background: #fff !important; overflow: hidden; box-shadow: none !important; }
.pl-page .filter-card .bg-light, .pl-page .filter-card .filter-stats-actions { background: var(--pl-mist) !important; border-color: var(--pl-line) !important; }
.pl-page label, .pl-page .col-form-label { font-size: 12.5px; font-weight: 700; color: var(--pl-muted); }
.pl-page .form-control, .pl-page .custom-select { min-height: 36px; border: 1.5px solid var(--pl-line); border-radius: 10px; font-size: 14px; font-weight: 600; color: var(--pl-ink); background-color: #fff; }
.pl-page .form-control:focus, .pl-page .custom-select:focus { border-color: var(--pl-orange); box-shadow: 0 0 0 3px rgba(238, 122, 34, 0.18); }
.pl-page .filter-badge-active, .pl-page .badge-info { background: var(--pl-orange-soft) !important; color: var(--pl-orange-dark) !important; border: 1px solid #F5CFAE; border-radius: 99px; font-weight: 700; padding: 5px 10px; }
.pl-page .badge { border-radius: 99px; font-weight: 700; }
.pl-page .badge-primary { background: var(--pl-orange); }
.pl-page .badge-success { background: var(--pl-teal); }
.pl-page .badge-danger { background: var(--pl-red); }
.pl-page .dropdown-menu { border: 1px solid var(--pl-line); border-radius: 12px; padding: 5px; box-shadow: 0 18px 40px -16px rgba(34, 31, 28, 0.45); font-family: inherit; }
.pl-page .dropdown-item { border-radius: 8px; padding: 7px 10px; font-size: 13.5px; font-weight: 600; color: var(--pl-ink); }
.pl-page .dropdown-item:hover, .pl-page .dropdown-item:focus { background: var(--pl-orange-soft); color: #B5520B; }
.pl-page .dropdown-divider { border-color: #ECE8E1; margin: 4px; }

.pl-modal.modal-content { border: 0; border-radius: 18px; overflow: hidden; box-shadow: 0 30px 70px -20px rgba(34, 31, 28, 0.5); }
.pl-modal .modal-header { background: var(--pl-ink); color: #fff; border: 0; padding: 14px 20px; box-shadow: inset 0 -3px 0 var(--pl-orange); }
.pl-modal .modal-header .modal-title { font-family: 'Sora', 'Manrope', sans-serif; font-size: 17px; font-weight: 700; }
.pl-modal .modal-header .close { color: #fff; opacity: 0.8; text-shadow: none; }
.pl-modal .modal-body { background: #F7F5F2; font-size: 14px; }
.pl-modal .modal-footer { background: #fff; border-top: 1px solid var(--pl-line); }
.pl-modal .btn { border-radius: 10px; font-weight: 700; }
.pl-modal .btn-primary, .pl-modal .btn-info { background: var(--pl-orange); border-color: var(--pl-orange); color: #fff; }
.pl-modal .btn-primary:hover:not(:disabled) { background: var(--pl-orange-dark); border-color: var(--pl-orange-dark); }
.pl-modal .btn-success { background: var(--pl-teal); border-color: var(--pl-teal); }
.pl-modal .btn-secondary { background: #fff; border-color: var(--pl-line); color: var(--pl-ink); }
.pl-modal .form-control, .pl-modal .custom-select { border: 1.5px solid var(--pl-line); border-radius: 10px; }
.pl-modal .form-control:focus, .pl-modal .custom-select:focus { border-color: var(--pl-orange); box-shadow: 0 0 0 3px rgba(238, 122, 34, 0.18); }

@media (max-width: 575.98px) {
  .pl-dialog.modal-dialog { margin: 0; max-width: 100%; min-height: 100%; }
  .pl-dialog .modal-content.pl-modal { border-radius: 0; min-height: 100vh; }
  .pl-page .form-control, .pl-page .custom-select, .pl-modal .form-control, .pl-modal .custom-select { font-size: 16px; }
}
@media (pointer: coarse) {
  .pl-page .compact-toolbar .toolbar-group .btn { min-height: 40px; }
}
</style>


// WEBPACK FOOTER //
// src/components/Products/ProductsListProducts.vue